import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Register from './components/Register';
import Subscribe from './components/Subscribe';
import Home from './components/Home';
import ResetPassword from './components/ResetPassword';
import ResetPasswordConfirm from './components/ResetPasswordConfirm';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import GradingPlusSettings from './components/GradingPlusSettings';
import FAQ from './components/FAQ';
import ThankYou from './components/ThankYou';
import coinLogo from './assets/COINLogo.png';
import placeholderImage from './assets/COINPlaceholderImage.jpg';
import ReactGA4 from 'react-ga4';
import { DescriptionDisplay, generateEbaySearchUrl, stripSportFromDescription } from './utils/ebayUtils';
import SavedFiltersSidebar from './components/SavedFiltersSidebar';
import SavedFiltersDrawer from './components/SavedFiltersDrawer';
import GradingTierEfficiencyTable from './components/GradingTierEfficiencyTable';
import CoinScoreLegend from './components/CoinScoreLegend';
import QuickFlipSearch from './components/QuickFlipSearch';
import { FeatureFlagsProvider, useFeatureFlags } from './contexts/FeatureFlagsContext';
import { buildGradingCriteria, criteriaForSavedFilter, displaySavedConditions, fetchAllGradingCards } from './utils/gradingQueryClient';
import PreviewSubscriptionBanner from './components/PreviewSubscriptionBanner';
import AdminConsole from './components/admin/AdminConsole';
import AdminRoute from './components/admin/AdminRoute';
import { isQuickFlipSearchEnabled } from './config/featureFlags';
import {
  DEFAULT_GRADING_FEE_MODE,
  GRADING_FEE_MODES,
  GRADING_FEE_MODE_LABELS,
  normalizeGradingFeeMode,
  shouldShowExpressCoinScoreWarning
} from './utils/gradingFeeModes';

const ProfitPlannerPage = React.lazy(() => import('./components/ProfitPlannerPage'));

ReactGA4.initialize('G-FWPEPWMP9V');

const INSIGHT_SECTION_TOOLTIPS = {
  playerInsights: "Recent player context, news, and performance notes that can influence card-market attention.",
  cardInsights: "Card-specific context for market timing and set age. These sections explain timing exposure and how easy clean raw copies may be to source."
};

const SectionInfoIcon = ({ label, text }) => (
  <span className="info-tooltip-wrapper" onClick={(event) => event.stopPropagation()}>
    <span className="info-icon" tabIndex={0} aria-label={`${label} information`}>i</span>
    <span className="info-tooltip-text">{text}</span>
  </span>
);


// Update this function to track page views and set page titles
const SendPageView = () => {
  const location = useLocation();
  const { profitPlannerEnabled } = useFeatureFlags();
  
  useEffect(() => {
    // Get page title based on route
    const getPageTitle = (pathname) => {
      switch (pathname) {
        case '/':
          return 'COIN - Home';
        case '/login':
          return 'COIN - Login';
        case '/register':
          return 'COIN - Register';
        case '/dashboard':
          if (location.search.includes('sport=Hockey')) return 'COIN - Hockey Dashboard';
          return 'COIN - Dashboard';
        case '/subscribe':
          return 'COIN - Subscribe';
        case '/faq':
          return 'COIN - FAQ';
        case '/terms':
          return 'COIN - Terms';
        case '/privacy':
          return 'COIN - Privacy';
        case '/settings/ai':
          return 'COIN - AI Connections';
        case '/search':
          return 'COIN - Search';
        case '/profit-planner':
          return profitPlannerEnabled ? 'COIN - Profit Planner' : 'COIN - Home';
        case '/grading':
          return 'COIN - Grading';
        case '/reset-password':
          return 'COIN - Reset Password';
        case '/admin':
          return 'COIN - Admin Console';
        default:
          return 'COIN';
      }
    };

    const pageTitle = getPageTitle(location.pathname);
    // Update document title
    document.title = pageTitle;
    
    // Send pageview to GA4 with the title
    ReactGA4.send({
      hitType: "pageview",
      page: location.pathname,
      title: pageTitle
    });
  }, [location, profitPlannerEnabled]);
  
  return null;
};

const RouteScrollReset = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
};

export function AppRoutes() {
  const { profitPlannerEnabled, coinMcpEnabled, gradingPlusCheckoutEnabled, loading: featuresLoading } = useFeatureFlags();
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const quickFlipSearchEnabled = isQuickFlipSearchEnabled();

  if (loading || featuresLoading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <>
      <RouteScrollReset />
      <SendPageView />
      <Routes>
        {/* Public routes that don't require auth check */}
        <Route path="/" element={<Home />} />
        <Route path="/grading" element={<Home gradingOnly />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reset-password/:token" element={<ResetPasswordConfirm />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        {(coinMcpEnabled || gradingPlusCheckoutEnabled) && <Route path="/settings/ai" element={isAuthenticated ? <GradingPlusSettings /> : <Navigate to="/login" state={{ from: location }} replace />} />}
        <Route path="/thank-you" element={<ThankYou />} />
        {quickFlipSearchEnabled && (
          <Route path="/search" element={<QuickFlipSearch />} />
        )}
        {profitPlannerEnabled && (
          <Route path="/profit-planner" element={
            <React.Suspense fallback={<div className="loading-container">Loading...</div>}>
              <ProfitPlannerPage />
            </React.Suspense>
          } />
        )}

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" state={{ from: location }} />}
        />
        <Route 
          path="/subscribe" 
          element={isAuthenticated ? <Subscribe /> : <Navigate to="/login" state={{ from: location }} />}
        />
        <Route
          path="/faq"
          element={isAuthenticated ? <FAQ /> : <Navigate to="/login" state={{ from: location }} />}
        />
        {/* Admin console. AdminRoute gates on the is_admin flag from
            /api/auth/check; every /api/admin endpoint re-checks the
            ADMIN_EMAILS allowlist server-side. */}
        <Route
          path="/admin"
          element={<AdminRoute><AdminConsole /></AdminRoute>}
        />
        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// Helper function to get the correct image source
const getImageSource = (imageUrl) => {
  if (imageUrl === "COIN_PLACEHOLDER") {
    return placeholderImage;
  }
  return imageUrl;
};

// Dashboard Component (previously App's main content)
function Dashboard() {
  const { profitPlannerEnabled, gradingQueryEnabled, coinMcpEnabled, gradingPlusCheckoutEnabled } = useFeatureFlags();
  const aiInsightsEnabled = (process.env.REACT_APP_ENABLE_AI_INSIGHTS || 'true').toLowerCase() === 'true';
  const quickFlipEnabled = isQuickFlipSearchEnabled();
  const [allCards, setAllCards] = useState([]); // New state to hold all fetched cards
  const [cards, setCards] = useState([]); // State to hold filtered cards
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latestCardsRequestRef = useRef(0);
  const gradingAbortRef = useRef(null);
  const [authError, setAuthError] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const location = useLocation();
  const { checkAuthStatus, isAuthenticated, user } = useAuth();
  const [filters, setFilters] = useState({
    description: '',
  });
  const [selectedSport, setSelectedSport] = useState('All');
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [uniqueYears, setUniqueYears] = useState([]);
  const [conditionField, setConditionField] = useState('');
  const [conditionType, setConditionType] = useState('');
  const [conditionValue, setConditionValue] = useState('');
  const [gradingFeeMode, setGradingFeeMode] = useState(DEFAULT_GRADING_FEE_MODE);
  const useSharedGradingQuery = gradingQueryEnabled && user?.is_subscribed === true;

  // New state for multiple filters
  const [additionalFilters, setAdditionalFilters] = useState([]);

  // New state for sorting
  const [sortField, setSortField] = useState(''); // Field to sort by
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Update this state declaration
  const [visibleColumns, setVisibleColumns] = useState({
    price_90d: true,
    price_count_30d: true,
    price_count_90d: true,
    card_gems: true,
    card_total_grades: true,
    psa9_grade_rate: true,
    psa9_total_grades: true,
    price_30d_psa9: true,
    grading_fee: true,
    price_diff: true,
    volatility_score: true
  });

  // Add this near your other state declarations
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Add this near your other state declarations
  const [columnOrder, setColumnOrder] = useState([
    'price_90d',
    'price_count_30d',
    'price_count_90d',
    'card_gems',
    'card_total_grades',
    'psa9_grade_rate',
    'psa9_total_grades',
    'price_30d_psa9',
    'price_diff',
    'grading_fee',
    'volatility_score'
  ]);

  // Add these new states in the Dashboard component
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [expandedGoatPlayers, setExpandedGoatPlayers] = useState({});
  const [activeGoatPlayerKey, setActiveGoatPlayerKey] = useState(null);
  const [activeGoatCard, setActiveGoatCard] = useState(null);
  const [goatCardDetailFlipped, setGoatCardDetailFlipped] = useState(false);
  const [top300CardIds, setTop300CardIds] = useState(new Set());

  // Add new state for filter modal steps
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterStep, setFilterStep] = useState(1); // 1: field, 2: condition, 3: value
  const [tempFilter, setTempFilter] = useState({ field: '', type: '', value: '' });
  const [inputValue, setInputValue] = useState(''); // Add this state for the input

  // Saved filters state
  const [showSavedFilters, setShowSavedFilters] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [filterName, setFilterName] = useState('');

  // Perplexity search state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchCard, setSearchCard] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [playerInsights, setPlayerInsights] = useState(null);
  const [cardInsights, setCardInsights] = useState(null);
  const [playerCitations, setPlayerCitations] = useState([]);
  const [cardCitations, setCardCitations] = useState([]);
  const [showSeasonTimingGuide, setShowSeasonTimingGuide] = useState(false);

  // Track desktop vs mobile for desktop-only table tweaks
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 767);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Check for Stripe session ID
    const query = new URLSearchParams(location.search);
    const sessionId = query.get('session_id');
    
    if (sessionId) {
      verifySubscription(sessionId);
    } else {
      fetchCards();
    }
  }, [location]);

  useEffect(() => {
    fetchCards(); // Fetch cards when the component mounts
  }, []);

  // Fetch saved filters when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchSavedFilters();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    setExpandedGoatPlayers({});
    setActiveGoatPlayerKey(null);
    setActiveGoatCard(null);
    setGoatCardDetailFlipped(false);
  }, [selectedSport]);

  const normalizeCardId = (cardId) => {
    if (cardId === null || cardId === undefined) return '';
    return String(cardId);
  };

  const updateTop300CardIds = (cardList) => {
    setTop300CardIds(new Set((cardList || []).map(card => normalizeCardId(card.card_id)).filter(Boolean)));
  };

  const fetchTop300CardIds = async () => {
    try {
      const response = await fetch('/api/cards?limit=300', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) return;

      const responseText = await response.text();
      const data = JSON.parse(responseText.replace(/:NaN/g, ':null'));
      updateTop300CardIds(data.cards || []);
    } catch (error) {
      console.error('Error fetching Top 300 card IDs:', error);
    }
  };

  const isTop300Card = (card) => top300CardIds.has(normalizeCardId(card?.card_id));

  const Top300Badge = () => (
    <span className="top-300-overlap-badge" title="Also appears on the Top 300 list">
      Top 300
    </span>
  );

  const getDisplayDescription = (card) => stripSportFromDescription(card?.description, card?.sport);

  const inferSportFromCardText = (card) => {
    const text = `${card?.set || ''} ${card?.description || ''}`.toLowerCase();
    if (!text.trim()) return card?.sport || '';

    if (text.includes('basketball') || text.includes(' nba ') || text.includes(' wnba ')) return 'Basketball';
    if (text.includes('baseball') || text.includes(' mlb ')) return 'Baseball';
    if (text.includes('football') || text.includes(' nfl ')) return 'Football';
    if (text.includes('hockey') || text.includes(' nhl ')) return 'Hockey';
    if (text.includes('soccer') || text.includes('uefa') || text.includes('fifa') || text.includes('mls')) return 'Soccer';
    if (text.includes('f1') || text.includes('formula 1') || text.includes('formula one')) return 'F1';
    if (text.includes('golf')) return 'Golf';
    if (text.includes('tennis')) return 'Tennis';
    if (text.includes('nascar')) return 'NASCAR';
    if (text.includes('pokemon')) return 'Pokemon';

    return card?.sport || '';
  };

  const getAdjustedCardValue = (card, field) => card?.[field];
  const getCardEbayUrl = (card) => card.ebay_search_url || generateEbaySearchUrl(card.description, card.sport);

  const getSortableCardValue = (card, field, mode = gradingFeeMode) => {
    let value = getAdjustedCardValue(card, field, mode);
    if (typeof value === 'string' && value.startsWith('$')) {
      value = value.replace(/[$,]/g, '');
    } else if (typeof value === 'string' && value.includes('%')) {
      value = value.replace('%', '');
    } else if (typeof value === 'string' && value.includes('x')) {
      value = value.replace('x', '');
    }

    const numberValue = parseFloat(value);
    return Number.isNaN(numberValue) ? value : numberValue;
  };

  const verifySubscription = async (sessionId) => {
    try {
      const response = await fetch('/api/payment/verify-subscription', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        await checkAuthStatus(); // Update auth context
        // Remove the session_id from the URL without refreshing
        window.history.replaceState({}, '', '/dashboard');
        // Now fetch the cards
        fetchCards();
      } else {
        setError('Subscription verification failed. Please contact support.');
        setLoading(false);
        navigate('/login'); // Redirect to login if verification fails
      }
    } catch (error) {
      console.error('Error verifying subscription:', error);
      setError('Error verifying subscription. Please contact support.');
      setLoading(false);
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const runSharedQuery = async ({ category = selectedSport, mode = gradingFeeMode,
    description = filters.description, conditions = additionalFilters,
    field = sortField || 'coin_score', order = sortOrder, savedCriteria = null,
    units = 'display' } = {}) => {
    gradingAbortRef.current?.abort();
    const controller = new AbortController();
    gradingAbortRef.current = controller;
    const requestId = latestCardsRequestRef.current + 1;
    latestCardsRequestRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const criteria = savedCriteria || buildGradingCriteria({
        category, gradingFeeMode: mode, description,
        additionalFilters: conditions, sortField: field, sortOrder: order
      });
      const result = await fetchAllGradingCards(criteria, { units, signal: controller.signal });
      if (latestCardsRequestRef.current !== requestId || controller.signal.aborted) return;
      setAllCards(result.cards);
      setCards(result.cards);
      setPreviewMode(false);
      setIsSubscribed(true);
      if (category === 'All') updateTop300CardIds(result.cards);
      setUniqueYears(Array.from(new Set(result.cards.flatMap(card => card.description?.match(/\b\d{4}\b/g) || []))));
    } catch (queryError) {
      if (controller.signal.aborted || latestCardsRequestRef.current !== requestId) return;
      setError(queryError.message);
      setAllCards([]);
      setCards([]);
    } finally {
      if (latestCardsRequestRef.current === requestId) setLoading(false);
    }
  };

  const fetchCards = async (mode = gradingFeeMode) => {
    if (useSharedGradingQuery) {
      return runSharedQuery({ category: selectedSport, mode });
    }
    gradingAbortRef.current?.abort();
    const requestId = latestCardsRequestRef.current + 1;
    latestCardsRequestRef.current = requestId;
    const activeGradingFeeMode = normalizeGradingFeeMode(mode);

    try {
        // Attempting to fetch cards...
        
        // First test the connection
        try {
            const pingResponse = await fetch('/api/ping', {
                credentials: 'include',
            });
        } catch (error) {
        }
        
        const queryParams = new URLSearchParams({
            limit: 300,
            sport: selectedSport,
            grading_fee_mode: activeGradingFeeMode
        });

        if (filters.player) queryParams.append('player', filters.player);
        
        const url = `/api/cards?${queryParams}`;
        
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch cards');
        }

        // Get the response text first
        const responseText = await response.text();
        
        // Replace NaN with null in the response text
        const validJsonText = responseText.replace(/:NaN/g, ':null');
        
        // Now parse the valid JSON
        const data = JSON.parse(validJsonText);

        if (latestCardsRequestRef.current !== requestId) {
            return;
        }
        
        console.log('Preview mode status:', data.preview_mode);
        console.log('Total cards received:', data.cards?.length);
        
        // Handle preview mode data limitation
        if (data.preview_mode === true) {  // Explicitly check for true
            console.log('Applying preview mode limitation');
            // If in preview mode, only show rows 55-60
            const previewCards = (data.cards || []).slice(54, 60); // 0-based index, so 54 to 60 gives us rows 55-60
            console.log('Preview cards count:', previewCards.length);
            updateTop300CardIds(data.cards || []);
            setAllCards(previewCards);
            setCards(previewCards);
            setPreviewMode(true);
        } else {
            console.log('Showing all cards');
            updateTop300CardIds(data.cards || []);
            setAllCards(data.cards || []);
            setCards(data.cards || []);
            setPreviewMode(false);
        }
        
        // Set subscription status from API response
        setIsSubscribed(data.is_subscribed);
        
        setLoading(false);

        // Step 2: Extract unique years from card descriptions
        const years = new Set();
        // Use the correct set of cards for processing
        const cardsToProcess = data.preview_mode === true ? (data.cards || []).slice(54, 60) : (data.cards || []);
        cardsToProcess.forEach(card => {
            const yearMatch = card.description.match(/\b\d{4}\b/);
            if (yearMatch) {
                years.add(yearMatch[0]);
            }
        });
        setUniqueYears(Array.from(years));
    } catch (error) {
        if (latestCardsRequestRef.current !== requestId) {
            return;
        }
        console.error('Error fetching cards:', error);
        setError(error.message);
        setLoading(false);
        setCards([]);
    }
};

  // Real-time filtering based on description input
  useEffect(() => {
    if (!allCards) return;
    
    const filteredCards = allCards.filter(card =>
        card.description.toLowerCase().includes(filters.description.toLowerCase())
    );
    setCards(filteredCards);
  }, [filters.description, allCards]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdditionalFilterChange = (index, e) => {
    const { name, value } = e.target;
    
    setAdditionalFilters(prevFilters => {
      const newFilters = [...prevFilters];
      newFilters[index] = {
        ...newFilters[index],
        [name]: value
      };
      return newFilters;
    });
  };

  const addAdditionalFilter = () => {
    // For mobile view, show the filter modal
    if (window.innerWidth <= 767) {
      setShowFilterModal(true);
      return;
    }

    // For desktop view, add a new empty filter
    const newFilter = { field: '', type: '', value: '' };
    setAdditionalFilters([...additionalFilters, newFilter]);
  };

  const resetFilters = () => {
    setFilters({
      description: '',
    });
    setAdditionalFilters([]); // Reset to empty array
    setConditionField('');
    setConditionType('');
    setConditionValue('');
    const activeSortField = sortField || 'coin_score';
    const activeSortOrder = sortOrder || 'desc';
    if (useSharedGradingQuery) {
      runSharedQuery({ description: '', conditions: [], field: activeSortField, order: activeSortOrder });
      return;
    }
    setCards(sortCardList(allCards, activeSortField, activeSortOrder));
  };

  const handleFilterSubmit = (e, pendingFilters = additionalFilters) => {
    e.preventDefault();
    if (useSharedGradingQuery) {
      runSharedQuery({ conditions: pendingFilters });
      return;
    }
    setLoading(true);
    
    // Start with all cards when applying filters
    let filteredCards = [...allCards];
    
    // Apply description filter if it exists
    if (filters.description) {
      filteredCards = filteredCards.filter(card =>
        card.description.toLowerCase().includes(filters.description.toLowerCase())
      );
    }
    
    // Apply additional filters
    if (pendingFilters.length > 0) {
      filteredCards = filteredCards.filter(card => {
        return pendingFilters.every(filter => {
          if (!filter.field || !filter.type || !filter.value) return true;
          
          // Special handling for volatility score text values
          if (filter.field === 'volatility_score') {
            const cardVolatility = Number(card[filter.field]) >= 0.249 ? 'High' : 'Normal';
            return cardVolatility === filter.value;
          }
          
          // Get the card value and normalize it
          let cardValue = getAdjustedCardValue(card, filter.field);
          
          // Handle different types of values
          if (typeof cardValue === 'string') {
            // Remove currency symbols and commas
            if (cardValue.startsWith('$')) {
              cardValue = cardValue.replace(/[$,]/g, '');
            }
            // Convert percentages to decimal
            if (cardValue.includes('%')) {
              cardValue = cardValue.replace('%', '');
            }
            // Remove 'x' from multiplier values
            if (cardValue.includes('x')) {
              cardValue = cardValue.replace('x', '');
            }
          }
          
          // Convert to number
          cardValue = parseFloat(cardValue);
          
          // Handle the input value
          let inputValue = filter.value;
          if (typeof inputValue === 'string') {
            // Remove any symbols from input
            inputValue = inputValue.replace(/[$,%x]/g, '');
          }
          inputValue = parseFloat(inputValue);

          // For percentage fields, if the input is a whole number (like 50), assume it's a percentage
          if (['card_gem_rate', 'psa9_grade_rate'].includes(filter.field) && inputValue > 1) {
            inputValue = inputValue / 100;
          }

          // Handle the comparison
          switch (filter.type) {
            case 'greaterThan': return cardValue > inputValue;
            case 'lessThan': return cardValue < inputValue;
            case 'equalTo': return Math.abs(cardValue - inputValue) < 0.0001; // Use small epsilon for float comparison
            case 'greaterThanOrEqualTo': return cardValue >= inputValue;
            case 'lessThanOrEqualTo': return cardValue <= inputValue;
            case 'isBetween':
              const [minValue, maxValue] = inputValue.split(',').map(v => parseFloat(v.replace(/[$,%x]/g, '')));
              return cardValue >= minValue && cardValue <= maxValue;
            default: return true;
          }
        });
      });
    }

    const activeSortField = sortField || 'coin_score';
    const activeSortOrder = sortOrder || 'desc';
    setCards(sortCardList(filteredCards, activeSortField, activeSortOrder));
    setLoading(false);
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch('/api/payment/create-portal-session', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No portal URL received');
      }
    } catch (error) {
      console.error('Error accessing billing portal:', error);
    }
  };

  // Perplexity search handler
  const handlePerplexitySearch = async (card, e) => {
    e.stopPropagation();

    if (!aiInsightsEnabled) {
      return;
    }

    if (!isSubscribed) {
      alert('An active subscription is required to use AI Market Insights.');
      return;
    }

    // Open modal immediately with card info
    setSearchCard(card);
    setShowSearchModal(true);
    setSearchLoading(true);
    setSearchError(null);
    setPlayerInsights(null);
    setCardInsights(null);
    setPlayerCitations([]);
    setCardCitations([]);

    // Track GA event
    ReactGA4.event('perplexity_search', {
      description: card.description,
      sport: card.sport || selectedSport
    });

    try {
      const response = await fetch('/api/card-insights', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          card_id: card.card_id
        })
      });

      const data = await response.json();

      if (response.status === 403) {
        setSearchError('An active subscription is required to use AI Market Insights.');
      } else if (!response.ok) {
        setSearchError(data.error || 'An error occurred. Please try again.');
      } else if (!data.available) {
        setSearchError(data.message || 'Insights for this card are being prepared. Please check back soon.');
      } else {
        setPlayerInsights(data.player_insights);
        setCardInsights(data.card_insights);
        setPlayerCitations(data.player_citations || []);
        setCardCitations(data.card_citations || []);
      }
    } catch (error) {
      console.error('Error fetching card insights:', error);
      setSearchError('Network error. Please check your connection and try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const sortCardList = (cardList, field, order, mode = gradingFeeMode) => {
    return [...cardList].sort((a, b) => {
      const aValue = getSortableCardValue(a, field, mode);
      const bValue = getSortableCardValue(b, field, mode);
      const aIsNumber = typeof aValue === 'number' && !Number.isNaN(aValue);
      const bIsNumber = typeof bValue === 'number' && !Number.isNaN(bValue);

      if (aIsNumber && bIsNumber) {
        return order === 'desc' ? bValue - aValue : aValue - bValue;
      }

      if (aValue === null || aValue === undefined || aValue === '') return 1;
      if (bValue === null || bValue === undefined || bValue === '') return -1;

      return order === 'desc'
        ? String(bValue).localeCompare(String(aValue))
        : String(aValue).localeCompare(String(bValue));
    });
  };

  // Sorting function
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc'; // Default to desc
    setSortField(field);
    setSortOrder(order);
    if (useSharedGradingQuery) {
      runSharedQuery({ field, order });
      return;
    }
    setCards(sortCardList(cards, field, order));
  };

  const handleGradingFeeModeChange = (mode) => {
    const nextMode = normalizeGradingFeeMode(mode);
    if (nextMode === gradingFeeMode) return;

    setGradingFeeMode(nextMode);
    setError(null);
    setCards([]);
    setAllCards([]);
    setLoading(true);

    if (useSharedGradingQuery) {
      runSharedQuery({ mode: nextMode });
      return;
    }

    if (selectedSport === 'All') {
      fetchCards(nextMode);
      return;
    }

    handleSportChange({
      preventDefault: () => {},
      target: { value: selectedSport }
    }, nextMode);
  };

  // Update the toggle function to show/hide the column selector
  const toggleColumnSelector = () => {
    setShowColumnSelector(prev => !prev);
  };

  // Update this function to change the button text based on current state
  const toggleAllColumns = () => {
    const allColumnsEnabled = Object.values(visibleColumns).every(value => value);
    
    setVisibleColumns(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = !allColumnsEnabled;
      });
      return newState;
    });
  };

  // Add this function after the toggleAllColumns function
  const toggleColumn = (columnName) => {
    setVisibleColumns(prev => {
      const newVisibility = !prev[columnName];
      
      // Update column order
      if (newVisibility) {
        // If turning on, add to end of columnOrder if not already present
        setColumnOrder(prevOrder => 
          prevOrder.includes(columnName) ? prevOrder : [...prevOrder, columnName]
        );
      }
      
      return {
        ...prev,
        [columnName]: newVisibility
      };
    });
  };

  // Add this component for the column selector
  const ColumnSelector = () => (
    <div className="column-options">
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.price_90d}
          onChange={() => toggleColumn('price_90d')}
        />
        90 Day PSA 10 Price
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.price_count_30d}
          onChange={() => toggleColumn('price_count_30d')}
        />
        30 Day PSA 10 Sales
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.price_count_90d}
          onChange={() => toggleColumn('price_count_90d')}
        />
        90 Day PSA 10 Sales
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.card_gems}
          onChange={() => toggleColumn('card_gems')}
        />
        PSA 10 Pop
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.card_total_grades}
          onChange={() => toggleColumn('card_total_grades')}
        />
        Total Pop
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.psa9_grade_rate}
          onChange={() => toggleColumn('psa9_grade_rate')}
        />
        PSA 9 Rate
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.psa9_total_grades}
          onChange={() => toggleColumn('psa9_total_grades')}
        />
        PSA 9 Pop
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.price_30d_psa9}
          onChange={() => toggleColumn('price_30d_psa9')}
        />
        30 Day PSA 9 Price
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.grading_fee}
          onChange={() => toggleColumn('grading_fee')}
        />
        Grading Fee
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.price_diff}
          onChange={() => toggleColumn('price_diff')}
        />
        PSA 10 Margin
      </label>
      <label>
        <input
          type="checkbox"
          checked={visibleColumns.volatility_score}
          onChange={() => toggleColumn('volatility_score')}
        />
        Volatility Score
      </label>
    </div>
  );

  // Update this section where lastUpdatedDate is used
  const lastUpdatedDate = cards.length > 0 
    ? Math.max(...cards.map(card => new Date(card.last_updated).getTime()))
    : null;

  // Add this helper function to get column headers
  const getColumnHeader = (columnName) => {
    const headers = {
      'price_90d': '90 Day PSA 10 Price',
      'price_count_30d': '30 Day PSA 10 Sales',
      'price_count_90d': '90 Day PSA 10 Sales',
      'card_gems': 'PSA 10 Pop',
      'card_total_grades': 'Total Pop',
      'psa9_grade_rate': 'PSA 9 Rate',
      'psa9_total_grades': 'PSA 9 Pop',
      'price_30d_psa9': '30 Day PSA 9 Price',
      'grading_fee': 'Grading Fee',
      'price_diff': 'PSA 10 Margin',
      'volatility_score': 'Volatility Score',
      'coin_grade_letter': 'COIN Grade'
    };
    return headers[columnName];
  };

  // Add this helper function to format column values
  const formatColumnValue = (value, columnName, card) => {
    // Handle null, undefined, and NaN cases first
    if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
      return 'N/A';
    }

    // Try to convert to number if it's a numeric field
    const num = typeof value === 'string' ? parseFloat(value) : value;
    
    switch(columnName) {
      case 'price_90d':
      case 'price_30d_psa9':
      case 'grading_fee':
      case 'price_diff':
        return isNaN(num) ? 'N/A' : `$${num.toFixed(2)}`;
      case 'psa9_grade_rate':
        return isNaN(num) ? 'N/A' : `${(num * 100).toFixed(1)}%`;
      case 'price_count_30d':
      case 'price_count_90d':
      case 'card_gems':
      case 'card_total_grades':
      case 'psa9_total_grades':
        return isNaN(num) ? '0' : `${num}`;
      case 'volatility_score':
        return isNaN(num) ? 'Normal' : (num >= 0.249 ? 'High' : 'Normal');
      case 'coin_grade_letter':
        return value || 'N/A';
      default:
        return value || 'N/A';
    }
  };

  const isGoatTrendingView = selectedSport === 'GOAT' || selectedSport === 'Trending';

  const formatCurrencyValue = (value) => {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? 'N/A' : '$' + numberValue.toFixed(2);
  };

  const formatScoreValue = (value) => {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? 'N/A' : numberValue.toFixed(2);
  };

  const formatPercentValue = (value, multiplier = 1) => {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? 'N/A' : (numberValue * multiplier).toFixed(1) + '%';
  };

  const formatDateValue = (value) => {
    if (!value) return 'N/A';
    return formatDateTime(value);
  };

  const goatCardColumns = [
    { id: 'row_rank', label: 'Rank' },
    { id: 'image', label: 'Card' },
    { id: 'description', label: 'Description' },
    { id: 'ebay', label: 'Link' },
    { id: 'number', label: 'Card Number' },
    { id: 'coin_score', label: 'COIN Score' },
    { id: 'value_multiplier', label: 'PSA 10 Multiplier' },
    { id: 'raw_price', label: 'Raw Price' },
    { id: 'price_30d', label: '30 Day PSA 10 Price' },
    { id: 'latest_price', label: 'Latest PSA 10 Price' },
    { id: 'card_gem_rate', label: 'Gem Rate' },
    ...columnOrder
      .filter(columnName => visibleColumns[columnName])
      .map(columnName => ({ id: columnName, label: getColumnHeader(columnName) }))
  ];

  const goatPlayerGroups = (() => {
    if (!isGoatTrendingView || !Array.isArray(cards)) return [];

    const groupsByPlayer = new Map();
    cards.forEach((card) => {
      const displaySport = inferSportFromCardText(card);
      const normalizedCard = displaySport && displaySport !== card.sport
        ? { ...card, sport: displaySport }
        : card;
      const playerName = normalizedCard.player_name || normalizedCard.player || 'Unknown Player';
      const listRank = Number(normalizedCard.list_rank) || 999;
      const key = `${listRank}-${playerName}`;

      if (!groupsByPlayer.has(key)) {
        groupsByPlayer.set(key, {
          key,
          playerName,
          listRank,
          cardsById: new Map()
        });
      }

      const group = groupsByPlayer.get(key);
      if (!group.cardsById.has(normalizedCard.card_id)) {
        group.cardsById.set(normalizedCard.card_id, normalizedCard);
      }
    });

    return Array.from(groupsByPlayer.values())
      .map((group) => {
        const groupedCards = Array.from(group.cardsById.values());
        const topCoinScore = Math.max(
          ...groupedCards.map((card) => Number(getAdjustedCardValue(card, 'coin_score'))).filter((value) => !Number.isNaN(value))
        );
        const sports = Array.from(new Set(groupedCards.map((card) => card.sport).filter(Boolean)));

        return {
          key: group.key,
          playerName: group.playerName,
          listRank: group.listRank,
          topCoinScore: Number.isFinite(topCoinScore) ? topCoinScore : null,
          sports,
          cards: groupedCards
        };
      })
      .filter((group) => group.cards.length >= 3)
      .sort((a, b) => a.listRank - b.listRank || a.playerName.localeCompare(b.playerName))
      .slice(0, 25)
      .map((group, index) => ({ ...group, displayRank: index + 1 }));
  })();

  const activeGoatPlayerGroup = goatPlayerGroups.find(group => group.key === activeGoatPlayerKey);

  const handleGoatPlayerToggle = (group) => {
    if (!isDesktop) {
      setActiveGoatPlayerKey(group.key);
      return;
    }

    setExpandedGoatPlayers(prev => ({
      ...prev,
      [group.key]: !prev[group.key]
    }));
  };

  const openGoatCardDetail = (card) => {
    setActiveGoatCard(card);
    setGoatCardDetailFlipped(false);
  };

  const PSA_MULTIPLIER_TOOLTIP_TEXT = 'Higher-value cards typically show lower multipliers due to their raw card price. A 1.45x PSA 10 Multiplier on a $1,000 raw card may still be a strong grading opportunity based on the profit potential, especially if the card is in clean condition.';

  const getGemRateClass = (value) => {
    const rate = Number(value) * 100;
    if (Number.isNaN(rate)) return '';
    if (rate >= 75) return 'gem-outstanding';
    if (rate >= 65) return 'gem-great';
    if (rate >= 55) return 'gem-good';
    if (rate >= 40) return 'gem-average';
    if (rate >= 25) return 'gem-below-avg';
    return 'gem-poor';
  };

  const getCurrentMarketPhase = (sport, description) => {
    if (!sport) return null;
    const month = new Date().getMonth();
    const calendar = getSportSeasonCalendar(sport, description);
    if (!calendar) return null;
    return calendar[month];
  };

  // Seasonal Price Cycle phases per month (0=Jan, 11=Dec)
  // Phases: 'offseason', 'rising', 'peak', 'performance'
  const getSportSeasonCalendar = (sport, description) => {
    const normalizedSport = String(sport || '').trim().toLowerCase();
    const desc = String(description || '').toLowerCase();

    // WNBA detection: check sport field or description for WNBA keywords
    const isWnba = normalizedSport === 'wnba' || desc.includes('wnba');

    if (isWnba) {
      return ['offseason', 'offseason', 'rising', 'peak', 'peak', 'performance', 'performance', 'performance', 'performance', 'offseason', 'offseason', 'offseason'];
    }
    if (normalizedSport === 'football') {
      return ['performance', 'offseason', 'offseason', 'offseason', 'rising', 'rising', 'peak', 'peak', 'performance', 'performance', 'performance', 'performance'];
    }
    if (normalizedSport === 'baseball') {
      return ['rising', 'peak', 'peak', 'performance', 'performance', 'performance', 'performance', 'performance', 'performance', 'performance', 'offseason', 'offseason'];
    }
    if (normalizedSport === 'basketball' || normalizedSport === 'hockey') {
      return ['performance', 'performance', 'performance', 'performance', 'performance', 'offseason', 'offseason', 'rising', 'peak', 'peak', 'performance', 'performance'];
    }
    return null;
  };

  const getSeasonPhaseForMonth = (sport, monthIndex, description) => {
    const calendar = getSportSeasonCalendar(sport, description);
    if (!calendar) return null;
    return calendar[monthIndex];
  };

  const getSetAgeInYears = (description) => {
    if (!description) return null;

    const yearMatch = String(description).match(/\b(19|20)\d{2}\b/);
    if (!yearMatch) return null;

    const setYear = Number(yearMatch[0]);
    if (Number.isNaN(setYear)) return null;

    const currentYear = new Date().getFullYear();
    const age = currentYear - setYear;
    if (age < 0) return null;

    return age;
  };

  const getSetAgeColor = (description) => {
    const age = getSetAgeInYears(description);
    if (age === null) return null;
    if (age <= 2) return 'dark-green';
    if (age >= 3 && age <= 4) return 'light-green';
    if (age >= 5) return 'yellow';
    return null;
  };

  const getSetAgeLabel = (color, age) => {
    if (!color || age === null) return null;
    if (color === 'dark-green' && age === 0) {
      return 'This is a first-year product — clean copies are widely available, though new sets often see fast PSA 10 population growth as grading volume is typically high early on.';
    }
    if (color === 'dark-green') {
      return 'This set is fairly recent and clean raw copies are generally still easy to find.';
    }
    if (color === 'light-green') {
      return 'This set is a few years old and sourcing clean copies may require a bit more selectivity.';
    }
    if (color === 'yellow') {
      return 'This is an older set where clean copies can be harder to find as it has been picked over for several years.';
    }
    return null;
  };

  const parseLlmStoplightSections = (insightsText) => {
    if (!insightsText) {
      return {
        season: null,
        setAge: null
      };
    }

    const lines = String(insightsText).split(/\r?\n/);
    let currentCycle = null;
    let projectedCycle = null;
    let setAge = null;

    lines.forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line) {
        return;
      }

      const currentMatch = line.match(/^CURRENT_MARKET_CYCLE\|(OFFSEASON|LOW|RISING|PEAK|PERFORMANCE_DRIVEN)\|(.+)$/i);
      if (currentMatch) {
        let color = currentMatch[1].toLowerCase().replace('_', '-');
        if (color === 'low') color = 'offseason';
        currentCycle = { color, text: currentMatch[2].trim() };
        return;
      }

      const projectedMatch = line.match(/^PROJECTED_MARKET_CYCLE\|(OFFSEASON|LOW|RISING|PEAK|PERFORMANCE_DRIVEN)\|(.+)$/i);
      if (projectedMatch) {
        let color = projectedMatch[1].toLowerCase().replace('_', '-');
        if (color === 'low') color = 'offseason';
        projectedCycle = { color, text: projectedMatch[2].trim() };
        return;
      }

      const setAgeMatch = line.match(/^SET_AGE\|(DARK_GREEN|LIGHT_GREEN|YELLOW)\|(.+)$/i);
      if (setAgeMatch) {
        setAge = {
          color: setAgeMatch[1].toLowerCase().replace('_', '-'),
          text: setAgeMatch[2].trim()
        };
        return;
      }
    });

    return {
      currentCycle,
      projectedCycle,
      setAge
    };
  };

  const getMultiplierClass = (value) => {
    const multiplier = Number(value);
    if (Number.isNaN(multiplier)) return '';
    if (multiplier >= 4) return 'mult-exceptional';
    if (multiplier >= 3) return 'mult-very-strong';
    if (multiplier >= 2) return 'mult-strong';
    if (multiplier >= 1.5) return 'mult-fair';
    return 'mult-weak';
  };

  const hasExpressWeakCoinScore = (card) => {
    return shouldShowExpressCoinScoreWarning(
      gradingFeeMode,
      getAdjustedCardValue(card, 'coin_score')
    );
  };

  const EXPRESS_WEAK_ROW_MESSAGE = "This card has a COIN Score below 1.25 at the $199 Express grading fee. It may still be worth considering, but additional caution is recommended.";

  const ExpressCoinScoreWarning = ({ card }) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    if (!hasExpressWeakCoinScore(card)) return null;

    return (
      <button
        type="button"
        className={`paused-tier-warning ${isTooltipOpen ? 'is-open' : ''}`}
        aria-label={EXPRESS_WEAK_ROW_MESSAGE}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsTooltipOpen(prev => !prev);
        }}
        onBlur={() => {
          setIsTooltipOpen(false);
        }}
      >
        <span aria-hidden="true">⚠</span>
        <span className="paused-tier-warning-tooltip">{EXPRESS_WEAK_ROW_MESSAGE}</span>
      </button>
    );
  };

  // Add this helper function to format the timestamp
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Create a date object and convert to EST
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  // Update the sport selection handler
  const handleSportChange = async (event, mode = gradingFeeMode) => {
    event.preventDefault(); // Prevent default form submission
    const selectedSport = event.target.value;
    const requestId = latestCardsRequestRef.current + 1;
    latestCardsRequestRef.current = requestId;
    const activeGradingFeeMode = normalizeGradingFeeMode(mode);

    setSelectedSport(selectedSport);
    setError(null);
    setCards([]);
    setAllCards([]);
    setLoading(true);

    if (useSharedGradingQuery) {
      if (selectedSport === 'GOAT' || selectedSport === 'Trending') fetchTop300CardIds();
      return runSharedQuery({ category: selectedSport, mode: activeGradingFeeMode });
    }
    gradingAbortRef.current?.abort();
    
    try {
        let response;
        if (selectedSport === 'All') {
            // Use the original fetchCards function for "All Cards" view
            const queryParams = new URLSearchParams({
                limit: 300,
                grading_fee_mode: activeGradingFeeMode
            });
            
            response = await fetch(`/api/cards?${queryParams}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });
        } else if (selectedSport === 'Vault') {
            let apiUrl = `/api/vault-cards?sport=Vault&sort_by=coin_score&sort_dir=desc&per_page=1500&grading_fee_mode=${encodeURIComponent(activeGradingFeeMode)}`;
            console.log(`Fetching vault cards from ${apiUrl}`);
            response = await fetch(apiUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });
        } else if (selectedSport === 'GOAT' || selectedSport === 'Trending') {
            const list = selectedSport === 'GOAT' ? 'goat' : 'trending';
            let apiUrl = `/api/goat-trending-cards?list=${list}&sort_by=coin_score&sort_dir=desc&per_page=300&grading_fee_mode=${encodeURIComponent(activeGradingFeeMode)}`;
            console.log(`Fetching ${selectedSport} cards from ${apiUrl}`);
            fetchTop300CardIds();
            response = await fetch(apiUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });
        } else {
            // Use sports-cards endpoint for specific sports
            let apiUrl = `/api/sports-cards?sport=${selectedSport}&sort_by=coin_score&sort_dir=desc&grading_fee_mode=${encodeURIComponent(activeGradingFeeMode)}`;
            if (selectedSport === 'Hockey') {
                apiUrl += '&per_page=50'; // Add limit for Hockey
            } else if (selectedSport === 'Pokemon') {
                apiUrl += '&per_page=500'; // Show top 500 for Pokemon only
            } else if (selectedSport === 'Other') {
                apiUrl += '&per_page=60'; // Show top 60 Soccer+ cards
            }
            
            // Add error handling and logging
            console.log(`Fetching sports cards for ${selectedSport} from ${apiUrl}`);
            try {
                response = await fetch(apiUrl, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                });
                
                if (!response.ok) {
                    console.error(`HTTP error! status: ${response.status}`);
                    console.error('Response headers:', response.headers);
                    const text = await response.text();
                    console.error('Response text:', text);
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            } catch (fetchError) {
                console.error('Fetch error:', fetchError);
                throw fetchError;
            }
        }

        // Get the response text first
        const responseText = await response.text();
        
        // Log the response text for debugging
        console.log('Response text:', responseText);
        
        // Replace NaN with null in the response text
        const validJsonText = responseText.replace(/:NaN/g, ':null');
        
        // Now parse the valid JSON
        const data = JSON.parse(validJsonText);

        if (latestCardsRequestRef.current !== requestId) {
            return;
        }
        
        console.log('Sport change - Preview mode status:', data.preview_mode);

        // Handle different data structures for All Cards vs specific sports
        const cardsData = selectedSport === 'All' ? data.cards : data.data;
        console.log('Sport change - Total cards received:', cardsData?.length);

        // Apply preview mode limitation consistently
        if (data.preview_mode === true) {
            console.log('Sport change - Applying preview mode limitation');
            const previewCards = (cardsData || []).slice(54, 60);
            console.log('Sport change - Preview cards count:', previewCards.length);
            if (selectedSport === 'All') {
              updateTop300CardIds(cardsData || []);
            }
            setAllCards(previewCards);
            setCards(previewCards);
            setPreviewMode(true);
        } else {
            console.log('Sport change - Showing all cards');
            if (selectedSport === 'All') {
              updateTop300CardIds(cardsData || []);
            }
            setAllCards(cardsData || []);
            setCards(cardsData || []);
            setPreviewMode(false);
        }
        
        // Set subscription status from API response
        setIsSubscribed(data.is_subscribed);
    } catch (error) {
        if (latestCardsRequestRef.current !== requestId) {
            return;
        }
        console.error('Error fetching cards:', error);
        setError(error.message);
    } finally {
        if (latestCardsRequestRef.current === requestId) {
            setLoading(false);
        }
    }
};

  const handleEliteIndexNewsClick = (event) => {
    event.preventDefault();
    handleSportChange({
      preventDefault: () => {},
      target: { value: 'Vault' }
    });
  };

  // Saved filters functions
  const fetchSavedFilters = async () => {
    try {
      const response = await fetch('/api/filters', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch saved filters');
      }

      const data = await response.json();
      setSavedFilters(data.filters || []);
    } catch (error) {
      console.error('Error fetching saved filters:', error);
      setSavedFilters([]);
    }
  };

  const saveCurrentFilter = async () => {
    if (!filterName.trim()) {
      return;
    }

    try {
      const criteria = useSharedGradingQuery ? buildGradingCriteria({
        category: selectedSport,
        gradingFeeMode,
        description: filters.description,
        additionalFilters,
        sortField,
        sortOrder,
        visibleColumns
      }) : {
        description: filters.description,
        additionalFilters: additionalFilters,
        visibleColumns: visibleColumns,
        sortField: sortField,
        sortOrder: sortOrder,
        gradingFeeMode: gradingFeeMode
      };

      const response = await fetch('/api/filters', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: filterName.trim(),
          criteria: criteria,
          ...(useSharedGradingQuery ? { units: 'display' } : {})
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save filter');
      }

      const newFilter = await response.json();
      setSavedFilters(prev => [newFilter, ...prev]);
      setFilterName('');
      
      // Show success message (optional)
      alert('Filter saved successfully!');
    } catch (error) {
      console.error('Error saving filter:', error);
      alert('Failed to save filter: ' + error.message);
    }
  };

  const loadSavedFilter = (filter) => {
    try {
      if (useSharedGradingQuery) {
        const { criteria, units, categoryLabel } = criteriaForSavedFilter(filter, selectedSport, gradingFeeMode);
        setSelectedSport(categoryLabel);
        setGradingFeeMode(normalizeGradingFeeMode(criteria.gradingFeeMode));
        setFilters({ description: criteria.description || '' });
        setAdditionalFilters(displaySavedConditions(criteria));
        if (criteria.visibleColumns) setVisibleColumns(criteria.visibleColumns);
        setSortField(criteria.sortField || 'coin_score');
        setSortOrder(criteria.sortOrder || 'desc');
        setShowSavedFilters(false);
        runSharedQuery({ category: categoryLabel, savedCriteria: criteria, units });
        return;
      }
      const criteria = filter.criteria;
      const savedGradingFeeMode = normalizeGradingFeeMode(criteria.gradingFeeMode || gradingFeeMode);
      
      // Restore filter state
      if (criteria.description !== undefined) {
        setFilters(prev => ({ ...prev, description: criteria.description }));
      }
      
      if (criteria.additionalFilters) {
        setAdditionalFilters(criteria.additionalFilters);
      }
      
      if (criteria.visibleColumns) {
        setVisibleColumns(criteria.visibleColumns);
      }
      
      if (criteria.sortField) {
        setSortField(criteria.sortField);
        setSortOrder(criteria.sortOrder || 'desc');
      }

      setGradingFeeMode(savedGradingFeeMode);
      
      // Close the sidebar/drawer
      setShowSavedFilters(false);
      
      // Apply the filters immediately using the criteria directly
      setLoading(true);
      
      // Start with all cards when applying filters
      let filteredCards = [...allCards];
      
      // Apply description filter if it exists
      if (criteria.description) {
        filteredCards = filteredCards.filter(card =>
          card.description.toLowerCase().includes(criteria.description.toLowerCase())
        );
      }
      
      // Apply additional filters
      if (criteria.additionalFilters && criteria.additionalFilters.length > 0) {
        filteredCards = filteredCards.filter(card => {
          return criteria.additionalFilters.every(filter => {
            if (!filter.field || !filter.type || !filter.value) return true;
            
            // Special handling for volatility score text values
            if (filter.field === 'volatility_score') {
              const cardVolatility = Number(card[filter.field]) >= 0.249 ? 'High' : 'Normal';
              return cardVolatility === filter.value;
            }
            
            // Get the card value and normalize it
            let cardValue = getAdjustedCardValue(card, filter.field, savedGradingFeeMode);
            
            // Handle different types of values
            if (typeof cardValue === 'string') {
              // Remove currency symbols and commas
              if (cardValue.startsWith('$')) {
                cardValue = cardValue.replace(/[$,]/g, '');
              }
              // Convert percentages to decimal
              if (cardValue.includes('%')) {
                cardValue = cardValue.replace('%', '');
              }
              // Remove 'x' from multiplier values
              if (cardValue.includes('x')) {
                cardValue = cardValue.replace('x', '');
              }
            }
            
            // Convert to number
            cardValue = parseFloat(cardValue);
            
            // Handle the input value
            let inputValue = filter.value;
            if (typeof inputValue === 'string') {
              // Remove any symbols from input
              inputValue = inputValue.replace(/[$,%x]/g, '');
            }
            inputValue = parseFloat(inputValue);

            // For percentage fields, if the input is a whole number (like 50), assume it's a percentage
            if (['card_gem_rate', 'psa9_grade_rate'].includes(filter.field) && inputValue > 1) {
              inputValue = inputValue / 100;
            }

            // Handle the comparison
            switch (filter.type) {
              case 'greaterThan': return cardValue > inputValue;
              case 'lessThan': return cardValue < inputValue;
              case 'equalTo': return Math.abs(cardValue - inputValue) < 0.0001; // Use small epsilon for float comparison
              case 'greaterThanOrEqualTo': return cardValue >= inputValue;
              case 'lessThanOrEqualTo': return cardValue <= inputValue;
              case 'isBetween':
                const [minValue, maxValue] = inputValue.split(',').map(v => parseFloat(v.replace(/[$,%x]/g, '')));
                return cardValue >= minValue && cardValue <= maxValue;
              default: return true;
            }
          });
        });
      }
      
      // Apply sorting if specified
      if (criteria.sortField) {
        filteredCards = filteredCards.sort((a, b) => {
          let aValue = getAdjustedCardValue(a, criteria.sortField, savedGradingFeeMode);
          let bValue = getAdjustedCardValue(b, criteria.sortField, savedGradingFeeMode);

          // Handle currency values
          if (typeof aValue === 'string' && aValue.startsWith('$')) {
            aValue = parseFloat(aValue.replace('$', '').replace(',', ''));
          } else if (typeof aValue === 'string' && aValue.includes('%')) {
            aValue = parseFloat(aValue.replace('%', '')) / 100;
          } else if (typeof aValue === 'string' && aValue.includes('x')) {
            aValue = parseFloat(aValue.replace('x', ''));
          } else {
            aValue = parseFloat(aValue);
          }

          if (typeof bValue === 'string' && bValue.startsWith('$')) {
            bValue = parseFloat(bValue.replace('$', '').replace(',', ''));
          } else if (typeof bValue === 'string' && bValue.includes('%')) {
            bValue = parseFloat(bValue.replace('%', '')) / 100;
          } else if (typeof bValue === 'string' && bValue.includes('x')) {
            bValue = parseFloat(bValue.replace('x', ''));
          } else {
            bValue = parseFloat(bValue);
          }

          if (typeof aValue === 'number' && typeof bValue === 'number') {
            const order = criteria.sortOrder === 'asc' ? 1 : -1;
            return order * (bValue - aValue);
          }

          if (typeof aValue === 'string') {
            const order = criteria.sortOrder === 'asc' ? 1 : -1;
            return order * bValue.localeCompare(aValue);
          }

          return 0;
        });
      }
      
      // Update the displayed cards
      setCards(filteredCards);
      setLoading(false);
    } catch (error) {
      console.error('Error loading filter:', error);
      setLoading(false);
      alert('Failed to load filter: ' + error.message);
    }
  };

  const deleteSavedFilter = async (filterId) => {
    try {
      const response = await fetch(`/api/filters/${filterId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete filter');
      }

      setSavedFilters(prev => prev.filter(f => f.id !== filterId));
    } catch (error) {
      console.error('Error deleting filter:', error);
      alert('Failed to delete filter: ' + error.message);
    }
};

  // Update the getCoinGradeClass function for the new confidence levels
  const getCoinGradeClass = (grade) => {
    if (!grade) return '';
    
    if (grade === 'High Confidence') {
      return 'High-Confidence';
    } else if (grade === 'Low Confidence') {
      return 'Low-Confidence';
    } else {
      return '';
    }
  };

  // Update the CardGrid component to use the shared function
  const CardGrid = ({ cards }) => {
    const [flippedCards, setFlippedCards] = useState({});

    const handleCardFlip = (cardId) => {
      setFlippedCards(prev => ({
        ...prev,
        [cardId]: !prev[cardId]
      }));
    };

    return (
      <div className="cards-grid">
        {cards.map((card, index) => (
          <div 
            className={`card-container ${flippedCards[card.card_id] ? 'flipped' : ''} ${selectedSport === 'Pokemon' && (card.description || '').toLowerCase().includes('japanese') ? 'japanese' : ''} ${hasExpressWeakCoinScore(card) ? 'paused-tier-weak-row' : ''}`}
            onClick={() => handleCardFlip(card.card_id)}
            key={card.card_id}
          >
            <div className="card-item">
                <div className="card-front">
                  <div className="card-rank">{index + 1}</div>
                  {aiInsightsEnabled && isSubscribed && (
                    <div className="insight-action-stack mobile">
                      <button
                        className="perplexity-search-btn mobile"
                        onClick={(e) => handlePerplexitySearch(card, e)}
                        title="See insights"
                        aria-label={`See insights for ${getDisplayDescription(card)}`}
                      >
                        See insights
                      </button>
                      <ExpressCoinScoreWarning card={card} />
                    </div>
                )}
                <img
                  src={getImageSource(card.image)}
                  alt={getDisplayDescription(card)}
                  className="card-image"
                />
                <DescriptionDisplay description={card.description} sport={card.sport} ebaySearchUrl={card.ebay_search_url} className="card-description" />
                <div className="card-number">Card Number: {card.number || 'N/A'}</div>
                
                <div className="card-details">
                  <div className="coin-score">
                    <span className="detail-label">COIN Score</span>
                    <span className="detail-value">
                      <span>{formatScoreValue(getAdjustedCardValue(card, 'coin_score'))}</span>
                    </span>
                  </div>
                  
                  <div className="card-detail-item">
                    <span className="detail-label">Raw Price</span>
                    <span className="detail-value">
                      {Number.isNaN(Number(card.raw_price)) ? 'N/A' : '$' + Number(card.raw_price).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="card-detail-item">
                    <span className="detail-label">30 Day PSA 10</span>
                    <span className="detail-value">
                      {Number.isNaN(Number(card.price_30d)) ? 'N/A' : '$' + Number(card.price_30d).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="card-detail-item">
                    <span className="detail-label">Latest Price</span>
                    <span className="detail-value">
                      {Number.isNaN(Number(card.latest_price)) ? 'N/A' : '$' + Number(card.latest_price).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="card-detail-item">
                    <span className="detail-label">
                      PSA 10 Multiplier
                      <span className="info-tooltip-wrapper" onClick={(event) => event.stopPropagation()}>
                        <span className="info-icon" tabIndex={0} aria-label="PSA 10 Multiplier information">i</span>
                        <span className="info-tooltip-text">{PSA_MULTIPLIER_TOOLTIP_TEXT}</span>
                      </span>
                    </span>
                    <span className={`detail-value ${getMultiplierClass(getAdjustedCardValue(card, 'value_multiplier'))}`}>
                      {formatScoreValue(getAdjustedCardValue(card, 'value_multiplier'))}x
                    </span>
                  </div>
                  
                  <div className="card-detail-item">
                    <span className="detail-label">PSA 10 Pop</span>
                    <span className="detail-value">
                      {card.card_gems || '0'}
                    </span>
                  </div>
                  
                  <div className="card-detail-item">
                    <span className="detail-label">Gem Rate</span>
                    <span className={`detail-value ${getGemRateClass(card.card_gem_rate)}`}>
                      {Number.isNaN(Number(card.card_gem_rate)) ? '0%' : (Number(card.card_gem_rate) * 100).toFixed(1) + '%'}
                    </span>
                  </div>
                </div>
                
                <div className="flip-instruction">
                  <span className="desktop-instruction">Flip for more details</span>
                  <span className="mobile-instruction">Tap for more details & Ebay</span>
                </div>
              </div>
              <div className="card-back">
                <div className="card-rank">{index + 1}</div>
                <button 
                  className="flip-back-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardFlip(card.card_id);
                  }}
                >
                  ×
                </button>
                <h3 className="card-description">
                  <DescriptionDisplay description={card.description} sport={card.sport} ebaySearchUrl={card.ebay_search_url} className="" />
                </h3>
                <div className="card-back-content">
                  <div className="back-detail-item">
                    <span className="back-detail-label">30 Day PSA 10 Sales</span>
                    <span className="back-detail-value">
                      {card.price_count_30d || '0'}
                    </span>
                  </div>
                  
                  <div className="back-detail-item">
                    <span className="back-detail-label">90 Day PSA 10</span>
                    <span className="back-detail-value">
                      {Number.isNaN(Number(card.price_90d)) ? 'N/A' : '$' + Number(card.price_90d).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="back-detail-item">
                    <span className="back-detail-label">90 Day PSA 10 Sales</span>
                    <span className="back-detail-value">
                      {card.price_count_90d || '0'}
                    </span>
                  </div>
                  
                  <div className="back-detail-item">
                    <span className="back-detail-label">PSA 10 Margin</span>
                    <span className="back-detail-value">
                      {formatCurrencyValue(getAdjustedCardValue(card, 'price_diff'))}
                    </span>
                  </div>
                  
                  <div className="back-detail-item">
                    <span className="back-detail-label">Total Pop</span>
                    <span className="back-detail-value">
                      {card.card_total_grades || '0'}
                    </span>
                  </div>
                  
                  <div className="back-detail-item">
                    <span className="back-detail-label">PSA 9 Rate</span>
                    <span className="back-detail-value">
                      {Number.isNaN(Number(card.psa9_grade_rate)) ? '0%' : (Number(card.psa9_grade_rate) * 100).toFixed(1) + '%'}
                    </span>
                  </div>
                  
                  <div className="back-detail-item">
                    <span className="back-detail-label">PSA 9 Pop</span>
                    <span className="back-detail-value">
                      {card.psa9_total_grades || '0'}
                    </span>
                  </div>
                  
                  <div className="back-detail-item">
                    <span className="back-detail-label">30 Day PSA 9 Price</span>
                    <span className="back-detail-value">
                      {Number.isNaN(Number(card.price_30d_psa9)) ? 'N/A' : '$' + Number(card.price_30d_psa9).toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="back-detail-item">
                    <span className="back-detail-label">Grading Fee</span>
                    <span className="back-detail-value">
                      {formatCurrencyValue(getAdjustedCardValue(card, 'grading_fee'))}
                    </span>
                  </div>
                  
                  <div 
                    className={`back-detail-item ${Number.isNaN(Number(card.volatility_score)) || Number(card.volatility_score) >= 0.249 ? 'high-volatility' : ''}`}
                    title={Number.isNaN(Number(card.volatility_score)) || Number(card.volatility_score) >= 0.249 ? 
                      'This card has seen high volatility in sale price over the last 30 days' : ''}
                  >
                    <span className="back-detail-label">Volatility Score</span>
                    <span className="back-detail-value">
                      {Number.isNaN(Number(card.volatility_score)) ? 'Normal' : (Number(card.volatility_score) >= 0.249 ? 'High' : 'Normal')}
                    </span>
                  </div>
                </div>
                
                {/* Mobile eBay Link Box - Outside grid container for full width */}
                <div className="mobile-ebay-link-container">
                  <a 
                    href={getCardEbayUrl(card)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mobile-ebay-link"
                    onClick={(e) => {
                      // Track the click event
                      ReactGA4.event('ebay_search_click', {
                        description: getDisplayDescription(card)
                      });
                    }}
                  >
                    Ebay Link
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const GoatTrendingCardRows = ({ cards, compact = false }) => {
    if (compact) {
      return (
        <div className="goat-card-mobile-list">
          {cards.map((card, index) => (
            <div
              className={`goat-card-mobile-row ${hasExpressWeakCoinScore(card) ? 'paused-tier-weak-row' : ''}`}
              key={card.card_id}
              role="button"
              tabIndex={0}
              onClick={() => openGoatCardDetail(card)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openGoatCardDetail(card);
                }
              }}
            >
              <img
                src={getImageSource(card.image)}
                alt={getDisplayDescription(card)}
                className="goat-card-mobile-image"
              />
              <div className="goat-card-mobile-body">
                <div className="goat-card-mobile-rank">#{index + 1}</div>
                <div className="goat-card-mobile-title-row">
                  <DescriptionDisplay description={card.description} sport={card.sport} ebaySearchUrl={card.ebay_search_url} className="goat-card-mobile-title" />
                  {isTop300Card(card) && <Top300Badge />}
                </div>
                <div className="goat-card-mobile-meta">
                  <span>{card.sport || 'N/A'}</span>
                  <span>#{card.number || 'N/A'}</span>
                </div>
                <div className="goat-card-mobile-stats">
                  <span>COIN {formatScoreValue(getAdjustedCardValue(card, 'coin_score'))}</span>
                  <span>{formatScoreValue(getAdjustedCardValue(card, 'value_multiplier'))}x</span>
                  <span>{card.card_gems || '0'} PSA 10</span>
                </div>
                <div className="goat-card-mobile-actions">
                  <a
                    href={getCardEbayUrl(card)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="goat-ebay-link"
                    onClick={(event) => {
                      event.stopPropagation();
                      ReactGA4.event('ebay_search_click', {
                        description: getDisplayDescription(card)
                      });
                    }}
                  >
                    Ebay Link
                  </a>
                  {aiInsightsEnabled && isSubscribed && (
                    <div className="goat-insight-action-stack">
                      <button
                        className="goat-insight-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePerplexitySearch(card, e);
                        }}
                        title="See insights"
                        aria-label={`See insights for ${getDisplayDescription(card)}`}
                      >
                        See insights
                      </button>
                      <ExpressCoinScoreWarning card={card} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="table-scroll goat-player-table-scroll">
        <table className="cards-table goat-cards-table">
          <thead>
            <tr>
              {goatCardColumns.map((column) => (
                <th
                  key={column.id}
                  className={`${column.id === 'coin_score' ? 'coin-score-column' : ''} ${
                    column.id === 'value_multiplier' ? 'multiplier-column' : ''
                  }`.trim() || undefined}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cards.map((card, index) => (
              <tr
                key={card.card_id}
                className={hasExpressWeakCoinScore(card) ? 'paused-tier-weak-row' : undefined}
              >
                {goatCardColumns.map((column) => {
                  if (column.id === 'row_rank') {
                    return <td key={column.id}>{index + 1}</td>;
                  }
                  if (column.id === 'image') {
                    return (
                      <td key={column.id}>
                        <img
                          src={getImageSource(card.image)}
                          alt={getDisplayDescription(card)}
                          style={{ width: '72px', height: 'auto' }}
                        />
                      </td>
                    );
                  }
                  if (column.id === 'description') {
                    return (
                      <td key={column.id} className="description-cell">
                        <div className="goat-description-content">
                          <span>{getDisplayDescription(card)}</span>
                          {isTop300Card(card) && <Top300Badge />}
                        </div>
                        {aiInsightsEnabled && isSubscribed && (
                          <div className="insight-action-stack desktop">
                            <button
                              className="perplexity-search-btn desktop"
                              onClick={(e) => handlePerplexitySearch(card, e)}
                              title="See insights"
                              aria-label={`See insights for ${getDisplayDescription(card)}`}
                            >
                              See insights
                            </button>
                            <ExpressCoinScoreWarning card={card} />
                          </div>
                        )}
                      </td>
                    );
                  }
                  if (column.id === 'ebay') {
                    return (
                      <td key={column.id}>
                        <a
                          href={getCardEbayUrl(card)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="goat-ebay-link"
                          onClick={() => {
                            ReactGA4.event('ebay_search_click', {
                              description: getDisplayDescription(card)
                            });
                          }}
                        >
                          Ebay Link
                        </a>
                      </td>
                    );
                  }

                  const isCurrencyColumn = [
                    'raw_price',
                    'latest_price',
                    'price_30d',
                    'price_90d',
                    'price_30d_psa9',
                    'grading_fee',
                    'price_diff',
                    'weighted_average'
                  ].includes(column.id);
                  const isCountColumn = [
                    'price_count_30d',
                    'price_count_90d',
                    'card_gems',
                    'card_total_grades',
                    'psa9_total_grades'
                  ].includes(column.id);
                  const value = getAdjustedCardValue(card, column.id);
                  let displayValue = value || 'N/A';

                  if (isCurrencyColumn) displayValue = formatCurrencyValue(value);
                  if (isCountColumn) displayValue = Number.isNaN(Number(value)) ? '0' : `${value}`;
                  if (column.id === 'value_multiplier') displayValue = `${formatScoreValue(value)}x`;
                  if (column.id === 'coin_score') displayValue = formatScoreValue(value);
                  if (column.id === 'card_gem_rate' || column.id === 'psa9_grade_rate') {
                    displayValue = formatPercentValue(value, 100);
                  }
                  if (column.id === 'price_perc_diff') displayValue = formatPercentValue(value);
                  if (column.id === 'volatility_score') displayValue = formatColumnValue(value, column.id, card);
                  if (column.id === 'last_sale_date' || column.id === 'last_updated') displayValue = formatDateValue(value);

                  return (
                    <td
                      key={column.id}
                      className={`${column.id === 'coin_score' ? 'coin-score-column' : ''} ${
                        column.id === 'value_multiplier' ? `multiplier-column ${getMultiplierClass(value)}` : ''
                      } ${column.id === 'card_gem_rate' ? getGemRateClass(value) : ''}`.trim() || undefined}
                      data-volatility={column.id === 'volatility_score' ? (Number(value) >= 0.249 ? 'High' : 'Normal') : undefined}
                      title={column.id === 'volatility_score' && Number(value) >= 0.249 ? 'This card has high price volatility' : ''}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const GoatTrendingPlayerList = () => (
    <div className="goat-player-list">
      {goatPlayerGroups.map((group) => {
        const isExpanded = !!expandedGoatPlayers[group.key];
        const groupSports = group.sports.length ? group.sports.join(', ') : 'N/A';

        return (
          <section className="goat-player-group" key={group.key}>
            <button
              type="button"
              className="goat-player-header"
              onClick={() => handleGoatPlayerToggle(group)}
              aria-expanded={isDesktop ? isExpanded : undefined}
            >
              <span className="goat-player-rank">{group.displayRank}</span>
              <span className="goat-player-name">{group.playerName}</span>
              <span className="goat-player-stat goat-player-count">{group.cards.length} cards</span>
              <span className="goat-player-stat goat-player-top-score">Top COIN {formatScoreValue(group.topCoinScore)}</span>
              <span className="goat-player-sports">{groupSports}</span>
              <span className="goat-player-chevron" aria-hidden="true">
                {isDesktop ? (isExpanded ? '-' : '+') : '>'}
              </span>
            </button>

            {isDesktop && isExpanded && (
              <div className="goat-player-panel">
                <GoatTrendingCardRows cards={group.cards} />
              </div>
            )}
          </section>
        );
      })}

      {!isDesktop && activeGoatPlayerGroup && (
        <div className="goat-drawer-overlay" onClick={() => {
          setActiveGoatPlayerKey(null);
          setActiveGoatCard(null);
        }}>
          <div className="goat-drawer" onClick={(event) => event.stopPropagation()}>
            <div className="goat-drawer-header">
              <div>
                <div className="goat-drawer-rank">#{activeGoatPlayerGroup.displayRank}</div>
                <h2>{activeGoatPlayerGroup.playerName}</h2>
              </div>
              <button
                type="button"
                className="goat-drawer-close"
                onClick={() => {
                  setActiveGoatPlayerKey(null);
                  setActiveGoatCard(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="goat-drawer-summary">
              <span>{activeGoatPlayerGroup.cards.length} cards</span>
              <span>Top COIN {formatScoreValue(activeGoatPlayerGroup.topCoinScore)}</span>
            </div>
            <GoatTrendingCardRows cards={activeGoatPlayerGroup.cards} compact />
          </div>
        </div>
      )}

      {!isDesktop && activeGoatCard && (
        <div className="goat-card-detail-overlay" onClick={() => setActiveGoatCard(null)}>
          <div className="goat-card-detail-drawer" onClick={(event) => event.stopPropagation()}>
            <div className="goat-drawer-header">
              <div>
                <div className="goat-drawer-rank">{activeGoatCard.player_name || activeGoatCard.player || 'Player'}</div>
                <h2>{getDisplayDescription(activeGoatCard)}</h2>
              </div>
              <button
                type="button"
                className="goat-drawer-close"
                onClick={() => setActiveGoatCard(null)}
                aria-label="Close card detail"
              >
                ×
              </button>
            </div>

            <div className={`goat-card-detail-content ${goatCardDetailFlipped ? 'flipped' : ''}`}>
              {!goatCardDetailFlipped ? (
                <>
                  <img
                    src={getImageSource(activeGoatCard.image)}
                    alt={getDisplayDescription(activeGoatCard)}
                    className="goat-card-detail-image"
                  />
                  {isTop300Card(activeGoatCard) && (
                    <div className="goat-card-detail-badge-row">
                      <Top300Badge />
                    </div>
                  )}
                  <div className="goat-card-detail-metrics">
                    <div className="coin-score">
                      <span className="detail-label">COIN Score</span>
                      <span className="detail-value">{formatScoreValue(getAdjustedCardValue(activeGoatCard, 'coin_score'))}</span>
                    </div>
                    <div className="card-detail-item">
                      <span className="detail-label">Raw Price</span>
                      <span className="detail-value">{formatCurrencyValue(activeGoatCard.raw_price)}</span>
                    </div>
                    <div className="card-detail-item">
                      <span className="detail-label">30 Day PSA 10</span>
                      <span className="detail-value">{formatCurrencyValue(activeGoatCard.price_30d)}</span>
                    </div>
                    <div className="card-detail-item">
                      <span className="detail-label">Latest Price</span>
                      <span className="detail-value">{formatCurrencyValue(activeGoatCard.latest_price)}</span>
                    </div>
                    <div className="card-detail-item">
                      <span className="detail-label">PSA 10 Multiplier</span>
                      <span className={`detail-value ${getMultiplierClass(getAdjustedCardValue(activeGoatCard, 'value_multiplier'))}`}>
                        {formatScoreValue(getAdjustedCardValue(activeGoatCard, 'value_multiplier'))}x
                      </span>
                    </div>
                    <div className="card-detail-item">
                      <span className="detail-label">PSA 10 Pop</span>
                      <span className="detail-value">{activeGoatCard.card_gems || '0'}</span>
                    </div>
                    <div className="card-detail-item">
                      <span className="detail-label">Gem Rate</span>
                      <span className={`detail-value ${getGemRateClass(activeGoatCard.card_gem_rate)}`}>
                        {formatPercentValue(activeGoatCard.card_gem_rate, 100)}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="goat-card-detail-fields">
                  {goatCardColumns
                    .filter(column => !['row_rank', 'image', 'description', 'ebay'].includes(column.id))
                    .map((column) => {
                      const value = getAdjustedCardValue(activeGoatCard, column.id);
                      let displayValue = value || 'N/A';
                      if ([
                        'raw_price',
                        'graded_price',
                        'latest_price',
                        'price_30d',
                        'price_90d',
                        'price_30d_psa9',
                        'grading_fee',
                        'price_diff',
                        'weighted_average'
                      ].includes(column.id)) displayValue = formatCurrencyValue(value);
                      if (column.id === 'value_multiplier') displayValue = `${formatScoreValue(value)}x`;
                      if (column.id === 'coin_score') displayValue = formatScoreValue(value);
                      if (column.id === 'card_gem_rate' || column.id === 'psa9_grade_rate') displayValue = formatPercentValue(value, 100);
                      if (column.id === 'price_perc_diff') displayValue = formatPercentValue(value);
                      if (column.id === 'volatility_score') displayValue = formatColumnValue(value, column.id, activeGoatCard);
                      if (column.id === 'last_sale_date' || column.id === 'last_updated') displayValue = formatDateValue(value);

                      return (
                        <div className="goat-card-detail-field" key={column.id}>
                          <span>{column.label}</span>
                          <strong>{displayValue}</strong>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            <div className="goat-card-detail-actions">
              <button
                type="button"
                className="goat-card-flip-button"
                onClick={() => setGoatCardDetailFlipped(prev => !prev)}
              >
                {goatCardDetailFlipped ? 'Show Front' : 'Flip for Details'}
              </button>
              <a
                href={getCardEbayUrl(activeGoatCard)}
                target="_blank"
                rel="noopener noreferrer"
                className="goat-ebay-link"
                onClick={() => {
                  ReactGA4.event('ebay_search_click', {
                    description: getDisplayDescription(activeGoatCard)
                  });
                }}
              >
                Ebay Link
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Add this helper function to get all sortable columns
  const getSortableColumns = () => {
    return [
      { id: 'coin_score', label: 'COIN Score' },
      { id: 'value_multiplier', label: 'PSA 10 Multiplier' },
      { id: 'raw_price', label: 'Raw Price' },
      { id: 'price_30d', label: '30 Day PSA 10 Price' },
      { id: 'latest_price', label: 'Latest PSA 10 Price' },
      { id: 'card_gem_rate', label: 'Gem Rate' },
      { id: 'price_90d', label: '90 Day PSA 10 Price' },
      { id: 'price_count_30d', label: '30 Day PSA 10 Sales' },
      { id: 'price_count_90d', label: '90 Day PSA 10 Sales' },
      { id: 'card_gems', label: 'PSA 10 Pop' },
      { id: 'card_total_grades', label: 'Total Pop' },
      { id: 'psa9_grade_rate', label: 'PSA 9 Rate' },
      { id: 'psa9_total_grades', label: 'PSA 9 Pop' },
      { id: 'price_30d_psa9', label: '30 Day PSA 9 Price' },
      { id: 'grading_fee', label: 'Grading Fee' },
      { id: 'volatility_score', label: 'Volatility Score' }
    ];
  };

  // Add this component for the sort menu
  const SortMenu = () => (
    <div className="sort-menu">
      <div className="sort-menu-header">
        <h3>Sort By</h3>
        <button 
          className="close-button" 
          onClick={() => setShowSortMenu(false)}
        >
          ×
        </button>
      </div>
      <div className="sort-options">
        {getSortableColumns().map(column => (
          <button
            key={column.id}
            className={`sort-option ${sortField === column.id ? 'active' : ''}`}
            onClick={() => {
              handleSort(column.id);
              setShowSortMenu(false);
            }}
          >
            {column.label}
            {sortField === column.id && (
              <span className="sort-direction">
                {sortOrder === 'asc' ? ' ↑' : ' ↓'}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  // Update the FilterModal component
  const FilterModal = () => {
    const handleFieldSelect = (field) => {
      setTempFilter(prev => ({ ...prev, field }));
      setFilterStep(2);
    };

    const handleConditionSelect = (type) => {
      setTempFilter(prev => ({ ...prev, type }));
      setFilterStep(3);
    };

    const handleValueChange = (e) => {
      setInputValue(e.target.value);
    };

    const handleValueSubmit = () => {
      if (inputValue) {
        const newFilter = { ...tempFilter, value: inputValue };
        
        // Add the new filter to additionalFilters
        setAdditionalFilters(prev => [...prev, newFilter]);
        
        // Apply the filters immediately
        handleFilterSubmit(new Event('submit'), [...additionalFilters, newFilter]);
        
        // Reset the form for next filter
        setFilterStep(1);
        setShowFilterModal(false);
        setTempFilter({ field: '', type: '', value: '' });
        setInputValue('');
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleValueSubmit();
      }
    };

    const renderStep = () => {
      switch (filterStep) {
        case 1:
          return (
            <div className="filter-menu">
              <div className="filter-menu-header">
                <h3>Select Field</h3>
                <button className="close-button" onClick={() => setShowFilterModal(false)}>×</button>
              </div>
              <div className="filter-options">
                <button onClick={() => handleFieldSelect('raw_price')}>Raw Price</button>
                <button onClick={() => handleFieldSelect('price_30d')}>30 Day PSA 10 Price</button>
                <button onClick={() => handleFieldSelect('latest_price')}>Latest PSA 10 Price</button>
                <button onClick={() => handleFieldSelect('price_90d')}>90 Day PSA 10 Price</button>
                <button onClick={() => handleFieldSelect('price_diff')}>PSA 10 Margin</button>
                <button onClick={() => handleFieldSelect('card_gem_rate')}>Gem Rate</button>
                <button onClick={() => handleFieldSelect('coin_score')}>COIN Score</button>
                <button onClick={() => handleFieldSelect('value_multiplier')}>PSA 10 Multiplier</button>
                <button onClick={() => handleFieldSelect('price_count_30d')}>30 Day PSA 10 Sales</button>
                <button onClick={() => handleFieldSelect('price_count_90d')}>90 Day PSA 10 Sales</button>
                <button onClick={() => handleFieldSelect('card_gems')}>PSA 10 Pop</button>
                <button onClick={() => handleFieldSelect('card_total_grades')}>Total Pop</button>
                <button onClick={() => handleFieldSelect('psa9_grade_rate')}>PSA 9 Rate</button>
                <button onClick={() => handleFieldSelect('psa9_total_grades')}>PSA 9 Pop</button>
                <button onClick={() => handleFieldSelect('price_30d_psa9')}>30 Day PSA 9 Price</button>
                <button onClick={() => handleFieldSelect('volatility_score')}>Volatility Score</button>
              </div>
            </div>
          );
        case 2:
          return (
            <div className="filter-menu">
              <div className="filter-menu-header">
                <h3>Select Condition</h3>
                <button className="close-button" onClick={() => setFilterStep(1)}>←</button>
              </div>
              <div className="filter-options">
                {tempFilter.field === 'volatility_score' ? (
                  <button onClick={() => handleConditionSelect('equalTo')}>Equal to</button>
                ) : (
                  <>
                    <button onClick={() => handleConditionSelect('greaterThan')}>Greater than</button>
                    <button onClick={() => handleConditionSelect('lessThan')}>Less than</button>
                    <button onClick={() => handleConditionSelect('equalTo')}>Equal to</button>
                    <button onClick={() => handleConditionSelect('greaterThanOrEqualTo')}>Greater than or equal to</button>
                    <button onClick={() => handleConditionSelect('lessThanOrEqualTo')}>Less than or equal to</button>
                  </>
                )}
              </div>
            </div>
          );
        case 3:
          return (
            <div className="filter-menu">
              <div className="filter-menu-header">
                <h3>Enter Value</h3>
                <button className="close-button" onClick={() => setFilterStep(2)}>←</button>
              </div>
              <form 
                className="filter-value-input"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleValueSubmit();
                }}
              >
                {tempFilter.field === 'volatility_score' ? (
                  <select
                    value={inputValue}
                    onChange={handleValueChange}
                    autoFocus
                  >
                    <option value="">Select Volatility</option>
                    <option value="High">High</option>
                    <option value="Normal">Normal</option>
                  </select>
                ) : (
                  <input
                    type="number"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    placeholder="Enter value"
                    value={inputValue}
                    onChange={handleValueChange}
                    autoFocus
                  />
                )}
                <button 
                  type="submit"
                  className="submit-button"
                  disabled={!inputValue}
                >
                  Add Filter
                </button>
              </form>
            </div>
          );
      }
    };

    return renderStep();
  };

  // Helper: render markdown emphasis (**bold**, *italic*) without HTML
  const renderFormattedText = (text, keyPrefix = 'fmt') => {
    if (!text) return text;
    const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return tokens.map((token, idx) => {
      if (token.startsWith('**') && token.endsWith('**')) {
        return <strong key={`${keyPrefix}-b-${idx}`}>{token.slice(2, -2)}</strong>;
      }
      if (token.startsWith('*') && token.endsWith('*')) {
        return <em key={`${keyPrefix}-i-${idx}`}>{token.slice(1, -1)}</em>;
      }
      return <span key={`${keyPrefix}-t-${idx}`}>{token}</span>;
    });
  };

  const renderTextWithBreaks = (text, keyPrefix = 'line') => {
    if (!text) return text;
    const lines = text.split('\n');
    return lines.flatMap((line, idx) => {
      const lineNodes = renderFormattedText(line, `${keyPrefix}-${idx}`);
      return idx === lines.length - 1
        ? lineNodes
        : [...lineNodes, <br key={`${keyPrefix}-br-${idx}`} />];
    });
  };

  // Helper: render summary text with clickable citation links and emphasis
  const renderSummaryWithCitations = (text, citations) => {
    if (!citations || citations.length === 0) {
      return renderTextWithBreaks(text);
    }
    // Split on citation patterns like [1], [2][3], etc.
    const parts = text.split(/(\[\d+\])/g);
    return parts.flatMap((part, i) => {
      const match = part.match(/^\[(\d+)\]$/);
      if (match) {
        const index = parseInt(match[1], 10) - 1; // citations are 1-indexed
        const url = citations[index];
        if (url) {
          return (
            <a
              key={`cite-${i}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="search-citation-link"
              title={url}
            >
              {part}
            </a>
          );
        }
      }
      return renderTextWithBreaks(part, `part-${i}`);
    });
  };

  // SearchModal component
  const SearchModal = () => {
    const parsedStoplight = parseLlmStoplightSections(cardInsights);
    const modalSport = searchCard?.sport || selectedSport;
    const cardDescription = searchCard?.description;

    // Phase-to-CSS class mapping (for current cycle - matches buying perspective)
    const phaseColorMap = {
      'offseason': 'offseason',
      'rising': 'rising',
      'peak': 'peak',
      'performance': 'performance',
      'performance-driven': 'performance',
    };

    // Current Market Cycle
    const fallbackCurrentPhase = getCurrentMarketPhase(modalSport, cardDescription);
    const currentPhase = fallbackCurrentPhase || parsedStoplight.currentCycle?.color || null;
    const MARKET_TIMING_GUIDANCE = 'Use this calendar as a general reference point for stronger and weaker periods in the sports card market throughout the year. Compare these timing windows with current PSA turnaround estimates and grading tiers when planning submissions. Keep in mind strong grading upside typically outweighs seasonal timing.';
    const currentColor = phaseColorMap[currentPhase] || currentPhase;
    const currentLabel = currentColor ? MARKET_TIMING_GUIDANCE : null;

    // Set Age
    const setAgeYears = getSetAgeInYears(cardDescription);
    const fallbackSetAgeColor = getSetAgeColor(cardDescription);
    const setAgeColor = fallbackSetAgeColor || parsedStoplight.setAge?.color || null;
    const useLlmSetAgeText = !!(
      parsedStoplight.setAge?.text &&
      parsedStoplight.setAge?.color &&
      parsedStoplight.setAge.color === setAgeColor
    );
    const setAgeLabel = useLlmSetAgeText
      ? parsedStoplight.setAge.text
      : getSetAgeLabel(setAgeColor, setAgeYears);
    const showStoplightSection = !searchLoading && (
      (currentColor && currentLabel) || (setAgeColor && setAgeLabel)
    );
    const seasonGuideSports = ['Football', 'Baseball', 'Basketball', 'Hockey', 'WNBA'];
    const seasonGuideMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const closeSearchModal = () => {
      setShowSearchModal(false);
      setShowSeasonTimingGuide(false);
    };

    return (
      <div className="search-modal-overlay" onClick={closeSearchModal}>
        <div className="search-modal-container" onClick={e => e.stopPropagation()}>
          <button className="search-modal-close" onClick={closeSearchModal}>&times;</button>
          <div className="search-modal-content">
            <div className="search-modal-card-info">
              {searchCard && (
                <>
                  <img
                    src={getImageSource(searchCard.image)}
                    alt={getDisplayDescription(searchCard)}
                    className="search-modal-card-image"
                  />
                  <div className="search-modal-card-meta">
                    <div className="search-modal-card-title">{getDisplayDescription(searchCard)}</div>
                    {searchCard.price_30d && !Number.isNaN(Number(searchCard.price_30d)) && (
                      <div className="search-modal-price-badge">
                        30 Day PSA 10: ${Number(searchCard.price_30d).toFixed(2)}
                      </div>
                    )}
                    {searchCard.description && (
                      <a
                        href={getCardEbayUrl(searchCard)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="search-modal-ebay-link"
                        onClick={() => {
                          ReactGA4.event('ebay_search_click', {
                            description: getDisplayDescription(searchCard),
                          });
                        }}
                      >
                        Ebay Link
                      </a>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="search-modal-insights-container">
              {searchCard?.grading_tier_efficiency && (
                <GradingTierEfficiencyTable tiers={searchCard.grading_tier_efficiency} />
              )}
              {searchLoading && (
                <div className="search-spinner-container">
                  <div className="search-spinner"></div>
                  <p>Loading insights...</p>
                </div>
              )}
              {searchError && (
                <div className="search-modal-error">{searchError}</div>
              )}
              {playerInsights && (
                <div className="search-modal-insight">
                  <h3 className="search-modal-insight-header section-header-with-info">
                    Player Insights
                    <SectionInfoIcon label="Player Insights" text={INSIGHT_SECTION_TOOLTIPS.playerInsights} />
                  </h3>
                  <div className="search-modal-result">
                    {renderSummaryWithCitations(playerInsights, playerCitations)}
                  </div>
                </div>
              )}
              {showStoplightSection && (
                <div className="stoplight-divider">
                  <h3 className="stoplight-header section-header-with-info">
                    Card Insights
                    <SectionInfoIcon label="Card Insights" text={INSIGHT_SECTION_TOOLTIPS.cardInsights} />
                  </h3>
                  {currentColor && currentLabel && (
                    <div className="stoplight-row">
                      <div className="stoplight-title-line">
                        <span className={`stoplight-dot stoplight-${currentColor}`} />
                        <span className="stoplight-label">Market Timing</span>
                      </div>
                      <span className="stoplight-text">{currentLabel}</span>
                      <button
                        type="button"
                        className="stoplight-info-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowSeasonTimingGuide(true);
                        }}
                        aria-label="Open market cycle guide"
                        title="View market cycle guide"
                      >
                        <svg
                          className="stoplight-calendar-icon"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <rect x="3" y="4" width="18" height="17" rx="2" />
                          <path d="M8 2v4M16 2v4M3 10h18" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {setAgeColor && setAgeLabel && (
                    <div className="stoplight-row">
                      <div className="stoplight-title-line">
                        <span className={`stoplight-dot stoplight-${setAgeColor}`} />
                        <span className="stoplight-label">Set Age</span>
                      </div>
                      <span className="stoplight-text">{setAgeLabel}</span>
                    </div>
                  )}
                </div>
              )}
              {!searchLoading && (playerInsights || showStoplightSection) && (
                <p className="insights-ai-disclaimer">Insights are generated by AI, and AI can occasionally make mistakes.</p>
              )}
            </div>
          </div>
        </div>
        {showSeasonTimingGuide && (
          <div className="season-guide-overlay" onClick={() => setShowSeasonTimingGuide(false)}>
            <div className="season-guide-modal" onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                className="season-guide-close"
                onClick={() => setShowSeasonTimingGuide(false)}
                aria-label="Close sport season guide"
              >
                &times;
              </button>
              <h3 className="season-guide-title">Market Timing</h3>
              <p className="season-guide-subtitle">
                Use this calendar as a general reference point for stronger and weaker periods in the sports card market throughout the year. Compare these timing windows with current PSA turnaround estimates and grading tiers when planning submissions. Keep in mind strong grading upside typically outweighs seasonal timing.
              </p>
              <div className="season-guide-table-wrap">
                <table className="season-guide-table">
                  <thead>
                    <tr>
                      <th>Sport</th>
                      {seasonGuideMonths.map((month) => (
                        <th key={month}>{month}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {seasonGuideSports.map((sport) => (
                      <tr key={sport}>
                        <td className="season-guide-sport">{sport}</td>
                        {seasonGuideMonths.map((_, monthIndex) => {
                          const phase = getSeasonPhaseForMonth(sport, monthIndex);
                          const phaseLabels = { offseason: 'Off.', rising: 'Rising', peak: 'Peak', performance: 'P.D.' };
                          const label = phaseLabels[phase] || '';
                          return (
                            <td
                              key={`${sport}-${monthIndex}`}
                              title={phase === 'offseason' ? 'Offseason — attention tends to move away from the sport, prices can be relatively softer' :
                                     phase === 'rising' ? 'Early preseason — card demand often starts building due to excitement for the upcoming season' :
                                     phase === 'peak' ? 'Preseason — market interest and pricing are often elevated with the upcoming season just around the corner' :
                                     phase === 'performance' ? 'In-season — prices tend to move based on player performance, news, injuries, and hype' : ''}
                            >
                              <span className={`season-guide-cell season-guide-${phase}`}>{label}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="season-guide-definitions">
                <h4 className="season-guide-def-title">Definitions</h4>
                <div className="season-guide-def-item">
                  <span className="season-guide-cell season-guide-performance">P.D.</span>
                  <span className="season-guide-def-text">Performance-Driven — During the season, prices tend to move based on player performance, news, injuries, and hype.</span>
                </div>
                <div className="season-guide-def-item">
                  <span className="season-guide-cell season-guide-offseason">Off.</span>
                  <span className="season-guide-def-text">Offseason — During the early offseason, attention tends to move away from the sport and card prices can be relatively softer than usual.</span>
                </div>
                <div className="season-guide-def-item">
                  <span className="season-guide-cell season-guide-rising">Rising</span>
                  <span className="season-guide-def-text">Early preseason when card demand often starts building due to excitement for the upcoming season.</span>
                </div>
                <div className="season-guide-def-item">
                  <span className="season-guide-cell season-guide-peak">Peak</span>
                  <span className="season-guide-def-text">During the preseason, market interest and pricing are often elevated with the upcoming season just around the corner.</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Update the ActiveFilters component
  const ActiveFilters = () => {
    // Only show active filters on mobile
    if (window.innerWidth <= 767) {
      const formatConditionType = (type) => {
        switch(type) {
          case 'greaterThan': return 'greater than';
          case 'lessThan': return 'less than';
          case 'equalTo': return 'equal to';
          case 'greaterThanOrEqualTo': return 'greater than or equal to';
          case 'lessThanOrEqualTo': return 'less than or equal to';
          default: return type;
        }
      };

      return (
        <div className="active-filters">
          {additionalFilters.map((filter, index) => {
            const displayName = getSortableColumns().find(col => col.id === filter.field)?.label;
            return (
              <div key={index} className="active-filter">
                {displayName} {formatConditionType(filter.type)} {filter.value}
                <button 
                  onClick={() => {
                    const newFilters = additionalFilters.filter((_, i) => i !== index);
                    setAdditionalFilters(newFilters);
                    handleFilterSubmit(new Event('submit'), newFilters);
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      );
    }

    // Return null for desktop view since we show the filter rows instead
    return null;
  };

  // Move this up near other handler functions, before the return statement
  const handleAddFilterClick = () => {
    if (window.innerWidth <= 767) { // Mobile view
      setShowFilterModal(true);
    } else {
      // Add a new empty filter for desktop view
      setAdditionalFilters(prev => [...prev, { field: '', type: '', value: '' }]);
    }
  };

  // Update the DesktopFilterRow component
  const DesktopFilterRow = ({ filter, index }) => {
    // Add a local state to handle the input value
    const [localValue, setLocalValue] = useState(filter.value || '');
    
    // Update local state when filter value changes from parent
    useEffect(() => {
      setLocalValue(filter.value || '');
    }, [filter.value]);
    
    // Handle local input changes
    const handleLocalChange = (e) => {
      const { value } = e.target;
      setLocalValue(value);
    };
    
    // Update parent state when input loses focus
    const handleBlur = () => {
      handleAdditionalFilterChange(index, {
        target: { name: 'value', value: localValue }
      });
    };
    
    return (
      <div className="filter-row">
        <select
          name="field"
          value={filter.field}
          onChange={(e) => handleAdditionalFilterChange(index, e)}
        >
          <option value="">Select Field</option>
          <option value="raw_price">Raw Price</option>
          <option value="price_30d">30 Day PSA 10 Price</option>
          <option value="latest_price">Latest PSA 10 Price</option>
          <option value="price_90d">90 Day PSA 10 Price</option>
          <option value="price_diff">PSA 10 Margin</option>
          <option value="card_gem_rate">Gem Rate</option>
          <option value="coin_score">COIN Score</option>
          <option value="value_multiplier">PSA 10 Multiplier</option>
          <option value="price_count_30d">30 Day PSA 10 Sales</option>
          <option value="price_count_90d">90 Day PSA 10 Sales</option>
          <option value="card_gems">PSA 10 Pop</option>
          <option value="card_total_grades">Total Pop</option>
          <option value="psa9_grade_rate">PSA 9 Rate</option>
          <option value="psa9_total_grades">PSA 9 Pop</option>
          <option value="price_30d_psa9">30 Day PSA 9 Price</option>
          <option value="volatility_score">Volatility Score</option>
        </select>
        <select
          name="type"
          value={filter.type}
          onChange={(e) => handleAdditionalFilterChange(index, e)}
        >
          <option value="">Select Condition</option>
          {filter.field === 'volatility_score' ? (
            <option value="equalTo">Equal to</option>
          ) : (
            <>
              <option value="greaterThan">Greater than</option>
              <option value="lessThan">Less than</option>
              <option value="equalTo">Equal to</option>
              <option value="greaterThanOrEqualTo">Greater than or equal to</option>
              <option value="lessThanOrEqualTo">Less than or equal to</option>
              <option value="isBetween">Between (inclusive)</option>
            </>
          )}
        </select>
        {filter.field === 'volatility_score' ? (
          <select
            name="value"
            value={localValue}
            onChange={handleLocalChange}
            onBlur={handleBlur}
          >
            <option value="">Select Volatility</option>
            <option value="High">High</option>
            <option value="Normal">Normal</option>
          </select>
        ) : (
          <input
            type="text"
            name="value"
            value={localValue}
            onChange={handleLocalChange}
            onBlur={handleBlur}
            placeholder={filter.type === 'isBetween' ? 'Minimum, maximum' : 'Enter value'}
          />
        )}
        <button
          type="button"
          onClick={() => {
            const newFilters = additionalFilters.filter((_, i) => i !== index);
            setAdditionalFilters(newFilters);
          }}
        >
          ×
        </button>
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="App">
      <header className="App-header">
        <div className="logo-container">
          <img src={coinLogo} alt="COIN Logo" />
        </div>
        <div className="nav-buttons">
          {quickFlipEnabled && <Link to="/search" className="faq-button dashboard-search-button">COIN Search</Link>}
          {profitPlannerEnabled && <Link to="/profit-planner" className="faq-button dashboard-search-button">Profit Planner</Link>}
          {(coinMcpEnabled || gradingPlusCheckoutEnabled) && <Link to="/settings/ai" className="faq-button dashboard-search-button">AI Connections</Link>}
          {/* The dashboard renders its own header rather than SharedHeader, so
              the Admin link has to be added here too. */}
          {user?.is_admin && <Link to="/admin" className="faq-button dashboard-search-button">Admin</Link>}
          {isSubscribed && (
            <button className="manage-subscription-button" onClick={handleManageSubscription}>
              <span className="full-text">Manage Subscription</span>
              <span className="short-text">Manage Sub</span>
            </button>
          )}
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      {previewMode && (
        <PreviewSubscriptionBanner />
      )}

      <section className="filters-section">
        <div className="sport-selector">
          <select 
            value={selectedSport} 
            onChange={handleSportChange}
            className="form-select"
          >
            <option value="All">Top 300</option>
            <option value="Vault">Elite Index</option>
            <option value="GOAT">Top GOATs</option>
            <option value="Trending">Top Trending</option>
            <option value="Baseball">Baseball</option>
            <option value="Basketball">Basketball</option>
            <option value="Football">Football</option>
            <option value="Hockey">Hockey</option>
            <option value="Other">Soccer+</option>
            <option value="Pokemon">Pokemon</option>
          </select>
        </div>
        <div className="grading-fee-toggle" role="group" aria-label="COIN score grading fee">
          <button
            type="button"
            className={gradingFeeMode === GRADING_FEE_MODES.VALUE ? 'active' : ''}
            onClick={() => handleGradingFeeModeChange(GRADING_FEE_MODES.VALUE)}
            title="Value: $39.99 per card, 90–100 business days, max insured value $500"
          >
            {GRADING_FEE_MODE_LABELS[GRADING_FEE_MODES.VALUE]}
          </button>
          <button
            type="button"
            className={gradingFeeMode === GRADING_FEE_MODES.STANDARD ? 'active' : ''}
            onClick={() => handleGradingFeeModeChange(GRADING_FEE_MODES.STANDARD)}
            title="Standard: $59.99 per card, 70–80 business days, max insured value $1,000"
          >
            {GRADING_FEE_MODE_LABELS[GRADING_FEE_MODES.STANDARD]}
          </button>
          <button
            type="button"
            className={gradingFeeMode === GRADING_FEE_MODES.PRIORITY ? 'active' : ''}
            onClick={() => handleGradingFeeModeChange(GRADING_FEE_MODES.PRIORITY)}
            title="Priority: $79.99 per card, 50–60 business days, max insured value $1,500"
          >
            {GRADING_FEE_MODE_LABELS[GRADING_FEE_MODES.PRIORITY]}
          </button>
          <button
            type="button"
            className={gradingFeeMode === GRADING_FEE_MODES.EXPRESS ? 'active' : ''}
            onClick={() => handleGradingFeeModeChange(GRADING_FEE_MODES.EXPRESS)}
            title="Express: $199 per card, 20–30 business days, max insured value $2,500"
          >
            {GRADING_FEE_MODE_LABELS[GRADING_FEE_MODES.EXPRESS]}
          </button>
        </div>
        <form onSubmit={handleFilterSubmit}>
          <div className="search-row">
            <input
              type="text"
              name="description"
              placeholder="Search by description"
              value={filters.description}
              onChange={handleFilterChange}
            />
          </div>
          
          {/* Mobile active filters */}
          <ActiveFilters />
          
          {/* Desktop filter rows */}
          <div className="desktop-filter-rows">
            {additionalFilters.map((filter, index) => (
              <DesktopFilterRow key={index} filter={filter} index={index} />
            ))}
          </div>
          
          <div className="filter-buttons">
            <button type="button" onClick={toggleColumnSelector}>Toggle Columns</button>
            {showColumnSelector && (
              <button type="button" onClick={toggleAllColumns}>
                {Object.values(visibleColumns).every(value => value) ? 'Hide All' : 'Show All'}
              </button>
            )}
            <button type="button" onClick={handleAddFilterClick}>+ Add Filter</button>
            <button type="submit">Apply Filters</button>
            <button type="button" onClick={resetFilters}>Reset</button>
            <button 
              type="button" 
              onClick={() => setShowSavedFilters(true)}
              className="saved-filters-button"
            >
              Saved Filters
            </button>
            <button 
              type="button" 
              className="sort-button mobile-only"
              onClick={() => setShowSortMenu(true)}
            >
              Sort By
            </button>
          </div>
          {showColumnSelector && <ColumnSelector />}
        </form>
      </section>

      <CoinScoreLegend />

      <div className="coin-news">
        <h2>COIN News</h2>
        {selectedSport === 'GOAT' ? (
          <>
            <p>
              GOAT (Greatest of All Time) refers only to the all-time most graded players. It does not reflect opinions on who is the "GOAT" in a given sport.
            </p>
            <p>
              Note: Cards can appear on the GOAT list without qualifying for the Top 300, which uses additional selection criteria.
            </p>
          </>
        ) : selectedSport === 'All' ? (
          <>
            <ul>
              <li><strong>NEW: PSA Grading Fee Options</strong> - Compare Value ($40), Standard ($60), Priority ($80), and Express ($199) grading economics, with Value selected by default for cards worth up to $500.</li>
            </ul>
          </>
        ) : null}
        <br />
        <p><em>Disclosure: Some links on this page are affiliate links, meaning we may earn a commission if you make a purchase, at no extra cost to you.</em></p>
        <br />
        <p>Love COIN? Get 50% off + plug into our full grading system & community. <a href="https://www.skool.com/ignite/about?ref=98005fa729944da68a0329b1672e9c75" target="_blank" rel="noopener noreferrer">Learn More Here</a></p>
        {selectedSport === 'Vault' && (
          <>
            <br />
            <p>
              <strong>About this list:</strong>
              {' '}
              This Elite Index includes higher-value cards that do not sell as often as the ones on COIN's main list.
              Because there are fewer sales, the available data may be more limited and prices may vary.{' '}
            </p>
            <br />
            <p>
              This list is intended to highlight cards for further evaluation and should be used alongside your own
              research and judgment before making any purchasing decisions.
            </p>
          </>
        )}
      </div>

      {/* Display the Last Updated date above the table */}
      {lastUpdatedDate && (
        <div className="last-updated">
          <strong>Last Updated:</strong> {formatDateTime(lastUpdatedDate)}
        </div>
      )}
      <br />
      <div className="contact-us">
        Contact Us: support@cardboardprofit.com
      </div>

      {isGoatTrendingView ? (
        <GoatTrendingPlayerList />
      ) : (
        <>
          {/* Add the CardGrid component */}
          <CardGrid cards={cards} />
          
          {/* Existing table view */}
          <div className="table-wrapper">
            <div className="table-scroll">
              <table className="cards-table">
                <thead>
                  <tr>
                    {/* Add Row column */}
                    <th>Rank</th>
                    {/* Fixed columns first */}
                    <th onClick={() => handleSort('card_id')}>
                      Card
                      {sortField === 'card_id' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    <th onClick={() => handleSort('description')}>
                      Description
                      {sortField === 'description' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    {isDesktop && (
                      <th>
                        Link
                      </th>
                    )}
                    <th onClick={() => handleSort('number')}>
                      Card Number
                      {sortField === 'number' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    <th className="coin-score-column" onClick={() => handleSort('coin_score')}>
                      COIN Score
                      {sortField === 'coin_score' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    <th className="multiplier-column" onClick={() => handleSort('value_multiplier')}>
                      PSA 10 Multiplier
                      <span className="info-tooltip-wrapper" onClick={(event) => event.stopPropagation()}>
                        <span className="info-icon" tabIndex={0} aria-label="PSA 10 Multiplier information">i</span>
                        <span className="info-tooltip-text">{PSA_MULTIPLIER_TOOLTIP_TEXT}</span>
                      </span>
                      {sortField === 'value_multiplier' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    <th onClick={() => handleSort('raw_price')}>
                      Raw Price
                      {sortField === 'raw_price' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    <th onClick={() => handleSort('price_30d')}>
                      30 Day PSA 10 Price
                      {sortField === 'price_30d' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    <th onClick={() => handleSort('latest_price')}>
                      Latest PSA 10 Price
                      {sortField === 'latest_price' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    <th onClick={() => handleSort('card_gem_rate')}>
                      Gem Rate
                      {sortField === 'card_gem_rate' && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                    </th>
                    
                    {/* Toggleable columns in order */}
                    {columnOrder.map(columnName => 
                      visibleColumns[columnName] && (
                        <th key={columnName} onClick={() => handleSort(columnName)}>
                          {getColumnHeader(columnName)}
                          {sortField === columnName && <span className="sort-arrow">{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(cards) && cards.map((card, index) => (
                    <tr
                      key={card.card_id}
                      className={hasExpressWeakCoinScore(card) ? 'paused-tier-weak-row' : undefined}
                    >
                      {/* Add Row number */}
                      <td>{index + 1}</td>
                      {/* Fixed columns first */}
                      <td>
                        <img 
                          src={getImageSource(card.image)} 
                          alt={getDisplayDescription(card)} 
                          style={{width: '100px', height: 'auto'}}
                        />
                      </td>
                      <td className={`${isDesktop ? 'description-cell' : ''} ${
                        isDesktop && selectedSport === 'Pokemon' && (card.description || '').toLowerCase().includes('japanese')
                          ? 'japanese-desc'
                          : ''
                      }`.trim() || undefined}>
                        {isDesktop ? (
                          <>
                            <span>{getDisplayDescription(card)}</span>
                            {aiInsightsEnabled && isSubscribed && (
                              <div className="insight-action-stack desktop">
                                <button
                                  className="perplexity-search-btn desktop"
                                  onClick={(e) => handlePerplexitySearch(card, e)}
                                  title="See insights"
                                  aria-label={`See insights for ${getDisplayDescription(card)}`}
                                >
                                  See insights
                                </button>
                                <ExpressCoinScoreWarning card={card} />
                              </div>
                            )}
                          </>
                        ) : (
                          <DescriptionDisplay description={card.description} sport={card.sport} ebaySearchUrl={card.ebay_search_url} className="" />
                        )}
                      </td>
                      {isDesktop && (
                        <td>
                          <a
                            href={getCardEbayUrl(card)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              backgroundColor: '#1976d2',
                              color: '#fff',
                              padding: '6px 10px',
                              borderRadius: '4px',
                              textDecoration: 'none',
                              fontWeight: 600
                            }}
                          >
                            Ebay Link
                          </a>
                        </td>
                      )}
                      <td>{card.number || 'N/A'}</td>
                      <td className="coin-score-column">{formatScoreValue(getAdjustedCardValue(card, 'coin_score'))}</td>
                      <td className={`multiplier-column ${getMultiplierClass(getAdjustedCardValue(card, 'value_multiplier'))}`.trim()}>{formatScoreValue(getAdjustedCardValue(card, 'value_multiplier'))}x</td>
                      <td>{Number.isNaN(Number(card.raw_price)) ? 'N/A' : '$' + Number(card.raw_price).toFixed(2)}</td>
                      <td>{Number.isNaN(Number(card.price_30d)) ? 'N/A' : '$' + Number(card.price_30d).toFixed(2)}</td>
                      <td>{Number.isNaN(Number(card.latest_price)) ? 'N/A' : '$' + Number(card.latest_price).toFixed(2)}</td>
                      <td className={getGemRateClass(card.card_gem_rate)}>{Number.isNaN(Number(card.card_gem_rate)) ? '0%' : (Number(card.card_gem_rate) * 100).toFixed(1) + '%'}</td>

                      {/* Toggleable columns in order */}
                      {columnOrder.map(columnName => 
                        visibleColumns[columnName] && (
                          <td 
                            key={columnName} 
                            data-volatility={columnName === 'volatility_score' ? (Number(getAdjustedCardValue(card, columnName)) >= 0.249 ? 'High' : 'Normal') : undefined}
                            title={columnName === 'volatility_score' && Number(getAdjustedCardValue(card, columnName)) >= 0.249 ?
                              'This card has high price volatility' : ''}
                          >
                            {formatColumnValue(getAdjustedCardValue(card, columnName), columnName, card)}
                          </td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add the sort menu modal */}
      {showSortMenu && (
        <div className="sort-menu-overlay" onClick={() => setShowSortMenu(false)}>
          <div className="sort-menu-container" onClick={e => e.stopPropagation()}>
            <SortMenu />
          </div>
        </div>
      )}

      {/* Add this to your JSX near the sort menu modal */}
      {showFilterModal && (
        <div className="filter-menu-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="filter-menu-container" onClick={e => e.stopPropagation()}>
            <FilterModal />
          </div>
        </div>
      )}

      {/* Search Modal */}
      {aiInsightsEnabled && showSearchModal && <SearchModal />}

      {/* Saved Filters Sidebar (Desktop) */}
      {isDesktop && (
        <SavedFiltersSidebar
          isOpen={showSavedFilters}
          onClose={() => setShowSavedFilters(false)}
          savedFilters={savedFilters}
          filterName={filterName}
          onFilterNameChange={setFilterName}
          onSaveFilter={saveCurrentFilter}
          onLoadFilter={loadSavedFilter}
          onDeleteFilter={deleteSavedFilter}
          currentSport={selectedSport}
        />
      )}

      {/* Saved Filters Drawer (Mobile) */}
      {!isDesktop && (
        <SavedFiltersDrawer
          isOpen={showSavedFilters}
          onClose={() => setShowSavedFilters(false)}
          savedFilters={savedFilters}
          filterName={filterName}
          onFilterNameChange={setFilterName}
          onSaveFilter={saveCurrentFilter}
          onLoadFilter={loadSavedFilter}
          onDeleteFilter={deleteSavedFilter}
          currentSport={selectedSport}
        />
      )}

      <footer className="app-footer">
        ©️{new Date().getFullYear()} Cardboard Edge LLC. All rights reserved.
        <br/>Cardboard Edge LLC
        <br/>5534 Saint Joe Road
        <br/>Fort Wayne, IN 46835
      </footer>
    </div>
  );
}

function App() {
  return (
    <FeatureFlagsProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </FeatureFlagsProvider>
  );
}

function RouteWrapper({ children }) {
  const location = useLocation();
  console.log('Current route:', location.pathname);
  return children;
}

export default App;
